import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  CSSProperties,
  DragEvent as ReactDragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  RefObject,
} from "react";
import {
  AlertTriangle,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Columns2,
  Copy,
  Crop,
  Eraser,
  GripVertical,
  Image as ImageIcon,
  Info,
  Italic,
  LayoutTemplate,
  Link2,
  Loader2,
  Mail,
  Minus,
  Monitor,
  Palette,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Plus,
  Redo2,
  Smartphone,
  Square,
  Trash2,
  Type,
  Underline as UnderlineIcon,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

type BlockType = "section" | "header" | "text" | "image" | "button" | "divider" | "footer" | "columns" | "html";
type Align = "left" | "center" | "right";
type Mode = "blocks" | "html" | "preview";
type Device = "desktop" | "mobile";

type ColumnContent = {
  image?: string;
  imageAlt?: string;
  imageHref?: string;
  title?: string;
  body?: string;
  buttonLabel?: string;
  buttonHref?: string;
  align?: Align;
  textColor?: string;
  backgroundColor?: string;
};

type BlockContent = {
  // Section container (marker that starts a new full-width "row" in the email)
  sectionTitle?: string;
  innerBg?: string;
  innerRadius?: number;
  innerWidth?: number;

  kicker?: string;
  title?: string;
  body?: string;
  src?: string;
  alt?: string;
  label?: string;
  href?: string;
  align?: Align;
  address?: string;
  unsub?: string;
  textColor?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontWeight?: 400 | 500 | 600 | 700;
  fontFamily?: string;
  lineHeight?: number;
  underline?: boolean;
  linkLabel?: string;
  linkHref?: string;
  buttonBg?: string;
  buttonTextColor?: string;
  widthPct?: number;
  imageLinkHref?: string;
  imageHeight?: number;
  autoHeight?: boolean;
  imageRadius?: number;
  imageFit?: "cover" | "contain";
  wrapperRadius?: number;
  paddingX?: number;
  paddingY?: number;

  // Columns block
  columns?: [ColumnContent, ColumnContent];
  columnGap?: number;

  // Raw HTML block (anything the importer can't map to a native block)
  html?: string;
};

type Block = {
  id: string;
  type: BlockType;
  content: BlockContent;
};

type Theme = {
  pageBg: string;
  cardBg: string;
  cardRadius: number;
  pagePadding: number;
  cardWidth: number;
  cardMode: "boxed" | "full";
};

type Doc = { blocks: Block[]; theme: Theme };

type PatchFn = (id: string, patch: BlockContent, coalesceKey?: string) => void;
type SetFn = (patch: BlockContent, key?: string) => void;
type UploadFn = (file: File) => Promise<string>;

type TemplateEditorProps = {
  value?: string;
  onChange?: (html: string) => void;
  /**
   * Optional: upload an image and resolve with its hosted URL.
   * Without it, images are embedded as data URLs (which Gmail and others block).
   */
  onUploadImage?: UploadFn;
};

/* ================================================================== */
/*  Constants                                                         */
/* ================================================================== */

const DEFAULT_THEME: Theme = {
  pageBg: "#f4f5f7",
  cardBg: "#ffffff",
  cardRadius: 12,
  pagePadding: 32,
  cardWidth: 600,
  cardMode: "boxed",
};

const COLOR_PALETTE = [
  "#111827",
  "#374151",
  "#6b7280",
  "#9ca3af",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#06b6d4",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#ffffff",
];

const HIGHLIGHT_PALETTE = ["#fee2e2", "#fef3c7", "#dcfce7", "#dbeafe", "#f3f4f6"];

const BODY_FONT = "Arial,sans-serif";
const HEADING_FONT = "Georgia,serif";

const FONT_OPTIONS: [string, string][] = [
  ["Arial", "Arial,sans-serif"],
  ["Helvetica", "Helvetica,Arial,sans-serif"],
  ["Verdana", "Verdana,Geneva,sans-serif"],
  ["Trebuchet", "'Trebuchet MS',sans-serif"],
  ["Georgia", "Georgia,serif"],
  ["Times", "'Times New Roman',Times,serif"],
  ["Courier", "'Courier New',monospace"],
];

const BLOCK_TYPES: { type: BlockType; label: string; icon: LucideIcon; desc: string }[] = [
  { type: "section", label: "Section", icon: LayoutTemplate, desc: "Full-width container" },
  { type: "header", label: "Header", icon: LayoutTemplate, desc: "Title + kicker" },
  { type: "text", label: "Text", icon: Type, desc: "Paragraph copy" },
  { type: "image", label: "Image", icon: ImageIcon, desc: "Resizable image" },
  { type: "columns", label: "Columns", icon: Columns2, desc: "Two side-by-side blocks" },
  { type: "button", label: "Button", icon: Square, desc: "Call to action" },
  { type: "divider", label: "Divider", icon: Minus, desc: "Hairline rule" },
  { type: "footer", label: "Footer", icon: Mail, desc: "Address + unsubscribe" },
  { type: "html", label: "HTML", icon: Code2, desc: "Custom code" },
];

const MERGE_TAGS = ["{{ subscriber.display_name }}","{{ subscriber.first_name }}", "{{ subscriber.last_name }}", "{{ subscriber.ctag }}", "{{ subscriber.email }}", "{{ app.name }}"];

const SHORTCUTS: [string, string][] = [
  ["⌘/Ctrl Z", "Undo"],
  ["⇧ ⌘/Ctrl Z", "Redo"],
  ["↑ / ↓", "Select previous / next"],
  ["Alt ↑ / ↓", "Move block"],
  ["⌘/Ctrl D", "Duplicate"],
  ["Delete", "Delete block"],
  ["Esc", "Deselect"],
];

const PLACEHOLDER_IMG = "https://placehold.co/560x260?text=Image";
const GMAIL_CLIP_BYTES = 102 * 1024;
const MAX_EMBED_BYTES = 300 * 1024;
const DND_MIME = "application/x-email-block";
const HEX6 = /^#[0-9a-f]{6}$/i;
const HISTORY_LIMIT = 100;
const COALESCE_MS = 700;

let uid = 0;
const nextId = () => `blk_${++uid}_${Date.now().toString(36)}`;

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/* ================================================================== */
/*  Small helpers                                                     */
/* ================================================================== */

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const escHtml = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escAttr = (v: string) => escHtml(v).replace(/"/g, "&quot;");
const bgOrUndefined = (v?: string) => (v && v !== "transparent" ? v : undefined);
const byteSize = (v: string) => new TextEncoder().encode(v).length;
const formatKb = (bytes: number) => `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
const blockMeta = (t: BlockType) => BLOCK_TYPES.find((b) => b.type === t) ?? BLOCK_TYPES[0];
const isBuilderHtml = (html?: string) => !!html && /data-block-type=/.test(html);

const isTypingTarget = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement &&
  (t.isContentEditable || t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT");

const defaultColumn = (title: string): ColumnContent => ({
  title,
  body: "Add a short description here.",
  align: "left",
  textColor: "#111827",
  backgroundColor: "transparent",
});

const defaultContent = (type: BlockType): BlockContent => {
  switch (type) {
    case "section":
      return {
        sectionTitle: "Section",
        backgroundColor: "transparent",
        paddingX: 16,
        paddingY: 24,
        innerBg: "#ffffff",
        innerRadius: 12,
        innerWidth: 600,
      };
    case "header":
      return {
        kicker: "Kicker",
        title: "Your headline goes here",
        align: "left",
        textColor: "#111827",
        fontSize: 26,
        fontWeight: 700,
        paddingX: 32,
        paddingY: 24,
        wrapperRadius: 0,
      };
    case "text":
      return {
        body: "Write your message here.",
        align: "left",
        textColor: "#111827",
        fontSize: 14,
        fontWeight: 400,
        underline: false,
        paddingX: 32,
        paddingY: 10,
        wrapperRadius: 0,
      };
    case "image":
      return {
        src: PLACEHOLDER_IMG,
        alt: "Description",
        widthPct: 100,
        imageFit: "cover",
        imageHeight: 260,
        paddingX: 32,
        paddingY: 10,
        wrapperRadius: 0,
      };
    case "columns":
      return {
        align: "left",
        backgroundColor: "transparent",
        paddingX: 32,
        paddingY: 16,
        wrapperRadius: 0,
        columnGap: 24,
        columns: [defaultColumn("Column one"), defaultColumn("Column two")],
      };
    case "button":
      return {
        label: "Read more",
        href: "https://example.com",
        align: "left",
        buttonBg: "#111827",
        buttonTextColor: "#ffffff",
        paddingX: 32,
        paddingY: 14,
        wrapperRadius: 0,
      };
    case "divider":
      return { paddingX: 32, paddingY: 6, wrapperRadius: 0 };
    case "footer":
      return {
        address: "Your company address",
        unsub: "Unsubscribe",
        align: "center",
        paddingX: 32,
        paddingY: 20,
        wrapperRadius: 0,
      };
    case "html":
      return {
        html: '<p style="margin:0;font-family:Arial,sans-serif;font-size:14px;color:#111827;">Custom HTML</p>',
        align: "left",
        paddingX: 32,
        paddingY: 10,
        wrapperRadius: 0,
      };
    default:
      return {};
  }
};

const cloneContent = (c: BlockContent): BlockContent => ({
  ...c,
  ...(c.columns ? { columns: [{ ...c.columns[0] }, { ...c.columns[1] }] as [ColumnContent, ColumnContent] } : {}),
});

const getDefaultBlocks = (): Block[] => [
  { id: nextId(), type: "section", content: defaultContent("section") },
  { id: nextId(), type: "header", content: defaultContent("header") },
  { id: nextId(), type: "text", content: defaultContent("text") },
  { id: nextId(), type: "button", content: defaultContent("button") },
  { id: nextId(), type: "footer", content: defaultContent("footer") },
];

const parsePx = (value?: string | null): number | undefined => {
  if (!value) return undefined;
  const num = Number.parseInt(value.replace("px", "").trim(), 10);
  return Number.isNaN(num) ? undefined : num;
};

const parseNumeric = (value?: string | null): number | undefined => {
  if (!value) return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const parseStyle = (styleString?: string | null): Record<string, string> => {
  if (!styleString) return {};
  return styleString.split(";").reduce<Record<string, string>>((acc, part) => {
    const [key, value] = part.split(":");
    if (!key || !value) return acc;
    acc[key.trim().toLowerCase()] = value.trim();
    return acc;
  }, {});
};

const stripTickWrappedUrl = (value?: string | null): string => {
  if (!value) return "";
  return value.trim().replace(/^`+|`+$/g, "");
};

/* ================================================================== */
/*  Sanitising                                                        */
/* ================================================================== */

const STRIP_SELECTOR =
  "script, iframe, frame, frameset, object, embed, applet, base, meta[http-equiv], input, select, textarea";
const URL_ATTRS = new Set(["href", "src", "action", "formaction", "background", "poster", "xlink:href"]);

/** Removes anything that can run code or navigate: scripts, frames, handlers, javascript: URLs. */
function stripDangerous(root: ParentNode) {
  root.querySelectorAll(STRIP_SELECTOR).forEach((n) => n.remove());
  root.querySelectorAll("form").forEach((form) => form.replaceWith(...Array.from(form.childNodes)));
  root.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "srcdoc") el.removeAttribute(attr.name);
      else if (URL_ATTRS.has(name) && /^\s*(javascript|vbscript|data:text\/html)/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
  });
}

/** For HTML blocks: safe markup with no <style> tags (they would leak into the editor UI). */
function sanitizeFragment(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(`<!DOCTYPE html><html><body>${html}</body></html>`, "text/html");
    stripDangerous(doc);
    doc.body.querySelectorAll("style, link, title, meta").forEach((n) => n.remove());
    return doc.body.innerHTML.trim();
  } catch {
    return "";
  }
}

const cleanImportedHtml = (html?: string): string => {
  if (!html?.trim()) return "";

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    stripDangerous(doc);

    doc.querySelectorAll('[data-field="body"]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.tagName.toLowerCase() === "p" && node.querySelector("p, div")) {
        const replacement = doc.createElement("div");
        replacement.setAttribute("data-field", "body");
        const style = node.getAttribute("style");
        if (style) replacement.setAttribute("style", style);
        replacement.innerHTML = node.innerHTML;
        node.replaceWith(replacement);
      }
    });

    doc.querySelectorAll("a, img").forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      ["href", "src"].forEach((attr) => {
        const value = node.getAttribute(attr);
        if (value) node.setAttribute(attr, stripTickWrappedUrl(value));
      });
    });

    return "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
  } catch {
    return html;
  }
};

const normalizeLinkUrl = (url: string): string => {
  const clean = stripTickWrappedUrl(url).trim();
  if (!clean) return "";
  if (/^https?:\/\//i.test(clean) || /^mailto:/i.test(clean) || /^tel:/i.test(clean)) return clean;
  if (/^www\./i.test(clean)) return `https://${clean}`;
  return clean;
};

const looksLikeUrl = (v: string) => /^(https?:\/\/|mailto:|tel:|#|\{\{|www\.)/i.test(v.trim());

const linkifyTextNodes = (root: HTMLElement) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();

  while (current) {
    if (current.parentElement && !current.parentElement.closest("a")) {
      textNodes.push(current as Text);
    }
    current = walker.nextNode();
  }

  const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

  textNodes.forEach((textNode) => {
    const text = textNode.textContent || "";
    if (!urlRegex.test(text)) return;
    urlRegex.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = urlRegex.exec(text)) !== null) {
      const [matched] = match;
      const start = match.index;
      if (start > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      }

      const anchor = document.createElement("a");
      anchor.href = normalizeLinkUrl(matched);
      anchor.textContent = matched;
      anchor.style.textDecoration = "underline";
      anchor.style.color = "#2563eb";
      fragment.appendChild(anchor);
      lastIndex = start + matched.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });
};

const normalizeBodyHtml = (html?: string): string => {
  if (!html?.trim()) return "";

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;

  wrapper.querySelectorAll('[data-field="body"]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.innerHTML.trim()) return;
    node.remove();
  });

  wrapper.querySelectorAll("a, img").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    ["href", "src"].forEach((attr) => {
      const value = node.getAttribute(attr);
      if (value) node.setAttribute(attr, normalizeLinkUrl(value));
    });
  });

  const flattenSingleWrapper = () => {
    const children = Array.from(wrapper.childNodes).filter((node) => {
      if (node.nodeType === Node.TEXT_NODE) return (node.textContent || "").trim().length > 0;
      return true;
    });

    if (children.length !== 1) return;
    const onlyChild = children[0];
    if (!(onlyChild instanceof HTMLDivElement)) return;
    wrapper.innerHTML = onlyChild.innerHTML;
  };

  flattenSingleWrapper();

  Array.from(wrapper.children).forEach((child) => {
    if (!(child instanceof HTMLElement)) return;
    if (child.tagName !== "DIV") return;
    if (child.querySelector("div, table, img")) return;

    const paragraph = document.createElement("p");
    const style = child.getAttribute("style");
    if (style) paragraph.setAttribute("style", style);
    paragraph.innerHTML = child.innerHTML || "<br>";
    child.replaceWith(paragraph);
  });

  linkifyTextNodes(wrapper);

  return wrapper.innerHTML
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/<p><br><\/p>\s*<p><br><\/p>/gi, "<p><br></p>")
    .trim();
};

/* ================================================================== */
/*  Block-list operations (pure)                                      */
/* ================================================================== */

/** A section marker owns every block after it up to the next marker. */
const sectionRange = (blocks: Block[], index: number): [number, number] => {
  if (blocks[index]?.type !== "section") return [index, index + 1];
  let end = index + 1;
  while (end < blocks.length && blocks[end].type !== "section") end++;
  return [index, end];
};

/** Sections can only land between groups — never split another section. */
const snapToGroupBoundary = (blocks: Block[], at: number) => {
  let i = clamp(at, 0, blocks.length);
  while (i < blocks.length && blocks[i].type !== "section") i++;
  return i;
};

function moveBlocks(blocks: Block[], from: number, to: number): Block[] {
  if (from < 0 || from >= blocks.length) return blocks;
  const [start, end] = sectionRange(blocks, from);
  const target = blocks[from].type === "section" ? snapToGroupBoundary(blocks, to) : clamp(to, 0, blocks.length);
  if (target >= start && target <= end) return blocks;
  const moving = blocks.slice(start, end);
  const rest = [...blocks.slice(0, start), ...blocks.slice(end)];
  const insertAt = target > start ? target - moving.length : target;
  rest.splice(insertAt, 0, ...moving);
  return rest;
}

function reorderBlocks(blocks: Block[], id: string, delta: number): Block[] {
  const index = blocks.findIndex((b) => b.id === id);
  if (index === -1) return blocks;

  if (blocks[index].type !== "section") {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return blocks;
    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    return next;
  }

  // Sections move as a whole group, hopping over the neighbouring group.
  const [start, end] = sectionRange(blocks, index);
  if (delta < 0) {
    if (start === 0) return blocks;
    let p = start - 1;
    while (p > 0 && blocks[p].type !== "section") p--;
    return moveBlocks(blocks, index, p);
  }
  if (end >= blocks.length) return blocks;
  const [, nextEnd] = sectionRange(blocks, end);
  return moveBlocks(blocks, index, nextEnd);
}

const applyThemePatch = (doc: Doc, patch: Partial<Theme>): Doc => {
  const sectionPatch: BlockContent = {};
  if (patch.cardBg !== undefined) sectionPatch.innerBg = patch.cardBg;
  if (patch.cardRadius !== undefined) sectionPatch.innerRadius = patch.cardRadius;
  if (patch.cardWidth !== undefined) sectionPatch.innerWidth = patch.cardWidth;
  if (patch.pagePadding !== undefined) sectionPatch.paddingY = patch.pagePadding;
  const touchesSections = Object.keys(sectionPatch).length > 0;
  return {
    theme: { ...doc.theme, ...patch },
    blocks: touchesSections
      ? doc.blocks.map((b) => (b.type === "section" ? { ...b, content: { ...b.content, ...sectionPatch } } : b))
      : doc.blocks,
  };
};

type Group = {
  markerId: string | null;
  markerIndex: number;
  marker: BlockContent;
  items: { block: Block; index: number }[];
};

function groupIntoSections(blocks: Block[], theme: Theme): Group[] {
  const base: BlockContent = {
    backgroundColor: "transparent",
    paddingY: theme.pagePadding,
    paddingX: 16,
    innerBg: theme.cardBg,
    innerRadius: theme.cardRadius,
    innerWidth: theme.cardWidth,
  };
  const groups: Group[] = [];
  let current: Group = { markerId: null, markerIndex: -1, marker: base, items: [] };

  blocks.forEach((block, index) => {
    if (block.type === "section") {
      if (current.markerId || current.items.length) groups.push(current);
      current = { markerId: block.id, markerIndex: index, marker: { ...base, ...block.content }, items: [] };
      return;
    }
    current.items.push({ block, index });
  });
  if (current.markerId || current.items.length) groups.push(current);
  return groups;
}

/* ================================================================== */
/*  HTML export / import                                              */
/* ================================================================== */

function exportHtml(blocks: Block[], theme: Theme): string {
  type Section = { marker: BlockContent; rows: string[]; explicit: boolean };

  const defaultMarker: BlockContent = {
    backgroundColor: "transparent",
    paddingX: 16,
    paddingY: theme.pagePadding,
    innerBg: theme.cardBg,
    innerRadius: theme.cardRadius,
    innerWidth: theme.cardWidth,
    sectionTitle: "Section",
  };

  const sections: Section[] = [];
  let current: Section = { marker: { ...defaultMarker }, rows: [], explicit: false };

  // Keep explicit (possibly empty) sections so a round trip never drops them.
  const pushSection = () => {
    if (!current.explicit && current.rows.length === 0) return;
    sections.push(current);
  };

  const renderColumnCell = (col: ColumnContent, index: number, gap: number): string => {
    const padStyle = index === 0 ? `padding-right:${gap / 2}px;` : `padding-left:${gap / 2}px;`;
    const color = col.textColor ?? "#111827";
    const img = col.image
      ? `<img data-field="col-${index}-image" src="${escAttr(stripTickWrappedUrl(col.image))}" alt="${escAttr(col.imageAlt ?? "")}" width="100%" style="display:block;width:100%;max-width:100%;border:0;border-radius:8px;margin-bottom:10px;" />`
      : "";
    return `<td class="ml-col" data-col="${index}" width="50%" valign="top" style="${padStyle}text-align:${col.align ?? "left"};background:${bgOrUndefined(col.backgroundColor) ?? "transparent"};">
      ${
        img && col.imageHref
          ? `<a data-field="col-${index}-image-link" href="${escAttr(stripTickWrappedUrl(col.imageHref))}" style="display:block;">${img}</a>`
          : img
      }
      ${
        col.title
          ? `<div data-field="col-${index}-title" style="font-family:Arial,sans-serif;font-size:16px;line-height:1.4;font-weight:700;color:${color};margin-bottom:6px;">${col.title}</div>`
          : ""
      }
      ${
        col.body
          ? `<div data-field="col-${index}-body" style="font-family:Arial,sans-serif;font-size:13px;line-height:1.6;color:${color};">${col.body}</div>`
          : ""
      }
      ${
        col.buttonLabel
          ? `<a data-field="col-${index}-button" href="${escAttr(stripTickWrappedUrl(col.buttonHref) || "#")}" style="display:inline-block;margin-top:10px;font-family:Arial,sans-serif;font-size:12px;font-weight:700;color:#ffffff;background:#111827;padding:8px 16px;border-radius:6px;text-decoration:none;">${col.buttonLabel}</a>`
          : ""
      }
    </td>`;
  };

  const renderBlockRow = (block: Block): string => {
    const c = block.content;
    const padX = c.paddingX ?? 32;
    const cls = padX > 16 ? ` class="ml-pad"` : "";
    const wrapperStyle = `padding:${c.paddingY ?? 10}px ${padX}px;text-align:${c.align ?? "left"};background:${c.backgroundColor ?? "transparent"};border-radius:${c.wrapperRadius ?? 0}px;`;

    if (block.type === "header") {
      return `<tr><td${cls} data-block-type="header" style="${wrapperStyle}">
        ${
          c.kicker
            ? `<div data-field="kicker" style="font-family:Arial,sans-serif;font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:${c.textColor ?? "#6b7280"};margin-bottom:6px;">${c.kicker}</div>`
            : ""
        }
        <div data-field="title" style="font-family:${escAttr(c.fontFamily || HEADING_FONT)};font-size:${c.fontSize ?? 26}px;line-height:${c.lineHeight ?? 1.3};color:${c.textColor ?? "#111827"};font-weight:${c.fontWeight ?? 700};text-decoration:${c.underline ? "underline" : "none"};">${c.title ?? ""}</div>
      </td></tr>`;
    }

    if (block.type === "text") {
      return `<tr><td${cls} data-block-type="text" style="${wrapperStyle}">
        <div class="ml-body" data-field="body" style="font-family:${escAttr(c.fontFamily || BODY_FONT)};font-size:${c.fontSize ?? 14}px;line-height:${c.lineHeight ?? 1.7};color:${c.textColor ?? "#111827"};font-weight:${c.fontWeight ?? 400};text-decoration:${c.underline ? "underline" : "none"};margin:0;">${c.body ?? ""}</div>
        ${
          c.linkLabel && c.linkHref
            ? `<p data-field="link" style="font-family:Arial,sans-serif;font-size:13px;line-height:1.7;margin:10px 0 0;"><a href="${escAttr(stripTickWrappedUrl(c.linkHref))}" style="color:${c.textColor ?? "#111827"};text-decoration:underline;">${c.linkLabel}</a></p>`
            : ""
        }
      </td></tr>`;
    }

    if (block.type === "image") {
      const src = stripTickWrappedUrl(c.src) || c.src || "";
      const height = c.autoHeight ? "auto" : `${c.imageHeight ?? 260}px`;
      const radius = c.imageRadius ?? 10;
      return `<tr><td${cls} data-block-type="image" data-width-pct="${c.widthPct ?? 100}" style="${wrapperStyle}">
        ${c.imageLinkHref ? `<a href="${escAttr(stripTickWrappedUrl(c.imageLinkHref))}" data-field="image-link" style="display:block;">` : ""}
        <img src="${escAttr(src)}" alt="${escAttr(c.alt ?? "")}" data-height="${c.autoHeight ? "auto" : c.imageHeight ?? 260}" data-fit="${c.imageFit ?? "cover"}" data-radius="${radius}" style="display:block;border:0;border-radius:${radius}px;width:${c.widthPct ?? 100}%;max-width:100%;margin:0 auto;height:${height};object-fit:${c.imageFit ?? "cover"};" />
        ${c.imageLinkHref ? `</a>` : ""}
      </td></tr>`;
    }

    if (block.type === "columns") {
      const gap = c.columnGap ?? 24;
      const cols = c.columns ?? [defaultColumn("Column one"), defaultColumn("Column two")];
      return `<tr><td${cls} data-block-type="columns" data-column-gap="${gap}" style="${wrapperStyle}">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
          ${renderColumnCell(cols[0], 0, gap)}
          ${renderColumnCell(cols[1], 1, gap)}
        </tr></table>
      </td></tr>`;
    }

    if (block.type === "button") {
      return `<tr><td${cls} data-block-type="button" style="${wrapperStyle}">
        <a href="${escAttr(stripTickWrappedUrl(c.href) || "#")}" style="display:inline-block;font-family:Arial,sans-serif;font-size:13px;font-weight:700;color:${c.buttonTextColor ?? "#ffffff"};background:${c.buttonBg ?? "#111827"};padding:10px 20px;border-radius:8px;text-decoration:none;">${c.label ?? ""}</a>
      </td></tr>`;
    }

    if (block.type === "divider") {
      return `<tr><td${cls} data-block-type="divider" style="${wrapperStyle}"><div style="border-top:1px solid #e5e7eb;"></div></td></tr>`;
    }

    if (block.type === "html") {
      return `<tr><td${cls} data-block-type="html" style="${wrapperStyle}">${c.html ?? ""}</td></tr>`;
    }

    return `<tr><td${cls} data-block-type="footer" style="${wrapperStyle}">
      <div data-field="address" style="font-family:Arial,sans-serif;font-size:12px;color:#6b7280;margin-bottom:4px;">${c.address ?? ""}</div>
      <div data-field="unsub" style="font-family:Arial,sans-serif;font-size:12px;color:#374151;text-decoration:underline;">${c.unsub ?? ""}</div>
    </td></tr>`;
  };

  for (const block of blocks) {
    if (block.type === "section") {
      pushSection();
      current = {
        marker: {
          ...defaultMarker,
          ...block.content,
          innerBg: block.content.innerBg ?? defaultMarker.innerBg,
          innerRadius: block.content.innerRadius ?? defaultMarker.innerRadius,
          innerWidth: block.content.innerWidth ?? defaultMarker.innerWidth,
        },
        rows: [],
        explicit: true,
      };
      continue;
    }
    current.rows.push(renderBlockRow(block));
  }
  pushSection();

  const outerRows = sections
    .map((section) => {
      const m = section.marker;
      return `<tr>
        <td class="ml-section" align="center" data-section-title="${escAttr(m.sectionTitle ?? "")}" style="background:${m.backgroundColor ?? "transparent"};padding:${m.paddingY ?? theme.pagePadding}px 16px;overflow-x:hidden;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:${m.innerWidth ?? theme.cardWidth}px;width:100%;background:${m.innerBg ?? theme.cardBg};border-radius:${m.innerRadius ?? theme.cardRadius}px;overflow:hidden;">
            ${section.rows.join("\n")}
          </table>
        </td>
      </tr>`;
    })
    .join("\n");

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="x-apple-disable-message-reformatting" />
    <style>
      .ml-body p { margin:0 0 12px; }
      .ml-body p:last-child { margin-bottom:0; }
      @media only screen and (max-width:600px) {
        .ml-section { padding-left:8px !important; padding-right:8px !important; }
        .ml-pad { padding-left:16px !important; padding-right:16px !important; }
        .ml-col { display:block !important; width:100% !important; padding-left:0 !important; padding-right:0 !important; }
        .ml-col + .ml-col { padding-top:16px !important; }
      }
    </style>
  </head>
  <body style="margin:0;padding:0;background:${theme.pageBg};">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${theme.pageBg};overflow-x:hidden;">
      ${outerRows}
    </table>
  </body>
</html>`;
}

/* ------------------------------------------------------------------ */
/*  Foreign HTML → blocks                                             */
/*                                                                    */
/*  Pasted HTML is rendered in a hidden, script-less iframe so we can */
/*  read real computed styles and layout (class-based CSS, bgcolor /  */
/*  align attributes, flex/table columns all resolve). The rendered   */
/*  tree is then walked and mapped onto native blocks. Anything that  */
/*  doesn't map cleanly becomes an "html" block with its styles       */
/*  inlined, so nothing is ever dropped.                              */
/* ------------------------------------------------------------------ */

const IMPORT_VIEWPORT = 800;
const MAX_CARD_WIDTH = 760;
const INVISIBLE_CHARS = /[\s\u00a0\u00ad\u034f\u200b-\u200d\u2060\ufeff]+/g;
const EDGE_BREAKS = /^(\s|&nbsp;|<br[^>]*>)+|(\s|&nbsp;|<br[^>]*>)+$/gi;
const HEADING_TAGS = /^H[1-6]$/;
const STRUCTURAL = "table, hr, h1, h2, h3, h4, h5, h6, video, audio, svg, canvas, picture";
const RAW_TAGS = new Set(["SVG", "VIDEO", "AUDIO", "CANVAS", "PICTURE"]);
const LAYOUT_TAGS = new Set(["TABLE", "TBODY", "THEAD", "TFOOT", "TR"]);
const KEEP_OUTER_TAGS = new Set(["A", "UL", "OL", "BLOCKQUOTE", "PRE"]);
const INHERITED_PROPS = [
  "color",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "line-height",
  "letter-spacing",
  "text-align",
  "text-transform",
];
const SIDES = ["top", "right", "bottom", "left"];

type Box = { top: number; left: number; right: number; bottom: number; width: number; height: number };
type ImportResult = { blocks: Block[]; theme: Theme };

const normText = (v: string | null | undefined) => (v ?? "").replace(INVISIBLE_CHARS, " ").trim();

function cssColorToHex(value: string | null | undefined): string | undefined {
  const v = (value ?? "").trim();
  if (!v) return undefined;
  if (v === "transparent") return "transparent";
  if (HEX6.test(v)) return v.toLowerCase();
  const m = v.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+)(%?))?\s*\)$/i);
  if (!m) return v;
  const alpha = m[4] === undefined ? 1 : Number(m[4]) / (m[5] ? 100 : 1);
  if (alpha === 0) return "transparent";
  return `#${[m[1], m[2], m[3]]
    .map((n) => clamp(Math.round(Number(n)), 0, 255).toString(16).padStart(2, "0"))
    .join("")}`;
}

/** Renders HTML in an off-screen iframe (scripts disabled) and hands its document to `fn`. */
function withRenderedDocument<T>(html: string, width: number, fn: (doc: Document, win: Window) => T): T | null {
  if (typeof document === "undefined" || !document.body) return null;
  const source = /^\s*<!doctype/i.test(html) ? html : `<!DOCTYPE html>${html}`;

  const attempt = (sandboxed: boolean): T | null => {
    const frame = document.createElement("iframe");
    if (sandboxed) frame.setAttribute("sandbox", "allow-same-origin");
    frame.setAttribute("aria-hidden", "true");
    frame.tabIndex = -1;
    frame.style.cssText = `position:fixed;left:-${width * 4}px;top:0;width:${width}px;height:800px;border:0;opacity:0;pointer-events:none;`;
    document.body.appendChild(frame);
    try {
      const doc = frame.contentDocument;
      const win = frame.contentWindow;
      if (!doc || !win) return null;
      doc.open();
      doc.write(source);
      doc.close();
      return fn(doc, win);
    } finally {
      frame.remove();
    }
  };

  try {
    return attempt(true);
  } catch {
    // Some browsers refuse document.write into a sandboxed frame. The HTML has
    // already been stripped of scripts and handlers, so an unsandboxed frame is safe.
    try {
      return attempt(false);
    } catch {
      return null;
    }
  }
}

function convertRenderedDocument(doc: Document, win: Window): ImportResult | null {
  const body = doc.body;
  if (!body) return null;

  /* ---------------------------- measuring ---------------------------- */

  const styles = new Map<Element, CSSStyleDeclaration>();
  const cs = (el: Element) => {
    let s = styles.get(el);
    if (!s) {
      s = win.getComputedStyle(el);
      styles.set(el, s);
    }
    return s;
  };
  const px = (v: string) => {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const ownBg = (el: Element) => cssColorToHex(cs(el).backgroundColor) ?? "transparent";
  const effectiveBg = (el: Element | null): string => {
    for (let n = el; n; n = n.parentElement) {
      const bg = ownBg(n);
      if (bg !== "transparent") return bg;
    }
    return "#ffffff";
  };
  const textOf = (el: Element) => normText((el as HTMLElement).innerText ?? el.textContent);
  const tagOf = (el: Element) => el.tagName.toUpperCase();

  const isHidden = (el: Element): boolean => {
    if (tagOf(el) === "BR") return false;
    const s = cs(el);
    if (s.display === "none" || s.visibility === "hidden" || s.visibility === "collapse") return true;
    if (s.opacity === "0") return true;
    if (s.maxHeight === "0px" && s.overflow === "hidden") return true;
    return el.getClientRects().length === 0;
  };

  const imgSize = (img: HTMLImageElement) => {
    const r = img.getBoundingClientRect();
    return {
      w: r.width || Number(img.getAttribute("width")) || 0,
      h: Number(img.getAttribute("height")) || r.height || 0,
    };
  };
  // Spacer gifs and tracking pixels.
  const isSpacerImg = (img: HTMLImageElement) => {
    const { w, h } = imgSize(img);
    return (w > 0 && w <= 3) || (h > 0 && h <= 3);
  };
  // Unknown size (not loaded, no attributes) counts as big: better a standalone image than a lost one.
  const isBigImg = (img: HTMLImageElement) => {
    if (isSpacerImg(img)) return false;
    const { w } = imgSize(img);
    return w === 0 || w >= 48;
  };
  const visibleImgs = (el: Element): HTMLImageElement[] =>
    (tagOf(el) === "IMG" ? [el as HTMLImageElement] : Array.from(el.querySelectorAll("img"))).filter(
      (img) => !isSpacerImg(img) && !isHidden(img)
    );
  const hasText = (el: Element) => !!textOf(el);
  const hasContent = (el: Element) =>
    RAW_TAGS.has(tagOf(el)) || hasText(el) || visibleImgs(el).length > 0 || !!el.querySelector("svg, video, canvas");

  /* ------------------------------- card ------------------------------ */

  // Descend through full-width wrappers until we reach the content column (the "card").
  const contentKids = (el: Element) => Array.from(el.children).filter((k) => !isHidden(k) && hasContent(k));
  let card: Element = body;
  for (let i = 0; i < 60; i++) {
    if (card !== body && card.getBoundingClientRect().width <= MAX_CARD_WIDTH) break;
    const kids = contentKids(card);
    if (kids.length !== 1) break;
    card = kids[0];
  }

  const cardRect = card.getBoundingClientRect();
  const wide = cardRect.width > MAX_CARD_WIDTH;
  const cardWidth = clamp(Math.round(wide ? 600 : cardRect.width), 320, 900);
  const cardLeft = wide ? cardRect.left + (cardRect.width - cardWidth) / 2 : cardRect.left;
  const cardBg = effectiveBg(card);
  const pageBg = card === body ? cardBg : effectiveBg(card.parentElement);
  const cardRadius = clamp(Math.round(px(cs(card).borderTopLeftRadius)), 0, 40);
  const pagePadding = clamp(Math.round(cardRect.top), 0, 80);

  /* ---------------------------- typography --------------------------- */

  const alignOf = (s: CSSStyleDeclaration): Align => {
    const t = s.textAlign;
    if (t.includes("center")) return "center";
    if (t.includes("right") || t === "end") return "right";
    return "left";
  };
  const weightOf = (s: CSSStyleDeclaration): BlockContent["fontWeight"] => {
    const n = Number(s.fontWeight) || (s.fontWeight === "bold" ? 700 : 400);
    return n >= 650 ? 700 : n >= 550 ? 600 : n >= 450 ? 500 : 400;
  };
  const typography = (s: CSSStyleDeclaration): BlockContent => {
    const size = px(s.fontSize);
    const lh = px(s.lineHeight);
    return {
      textColor: cssColorToHex(s.color),
      fontSize: Math.round(size) || undefined,
      fontWeight: weightOf(s),
      fontFamily: s.fontFamily.replace(/"/g, "'") || undefined,
      lineHeight: lh && size ? Math.round((lh / size) * 100) / 100 : undefined,
      align: alignOf(s),
      underline: false,
    };
  };

  /* ------------------------- style inlining -------------------------- */

  const formatValue = (prop: string, v: string) => {
    if (prop.includes("color")) return cssColorToHex(v) ?? v;
    if (prop === "font-family") return v.replace(/"/g, "'");
    if (prop === "text-align") return v.replace(/^-webkit-/, "");
    return v;
  };

  /**
   * Clones `src` with its computed look written into inline styles, so the markup
   * survives without the original <style> sheet (and without the editor's CSS reset).
   * mode: "full" = root gets every inherited property, "diff" = root only what differs
   * from its parent, "skip" = root untouched (its look is captured by block props).
   */
  const inlineStyles = (src: Element, mode: "full" | "diff" | "skip"): Element => {
    const clone = src.cloneNode(true) as Element;
    const srcs = [src, ...Array.from(src.querySelectorAll("*"))];
    const dsts = [clone, ...Array.from(clone.querySelectorAll("*"))];
    const drop: Element[] = [];

    srcs.forEach((s, i) => {
      const d = dsts[i] as HTMLElement | undefined;
      if (!d || !("style" in d)) return;
      const tag = tagOf(s);
      if (tag === "STYLE" || tag === "LINK" || tag === "META" || tag === "TITLE") {
        drop.push(d);
        return;
      }
      d.removeAttribute("class");
      d.removeAttribute("id");
      if (i === 0 && mode === "skip") return;
      if (i > 0 && (isHidden(s) || (tag === "IMG" && isSpacerImg(s as HTMLImageElement)))) {
        drop.push(d);
        return;
      }

      const st = cs(s);
      const parent = i === 0 ? (mode === "full" || !s.parentElement ? null : cs(s.parentElement)) : cs(s.parentElement as Element);

      for (const prop of INHERITED_PROPS) {
        const v = st.getPropertyValue(prop);
        if (v && (!parent || parent.getPropertyValue(prop) !== v)) d.style.setProperty(prop, formatValue(prop, v));
      }
      const bg = cssColorToHex(st.backgroundColor);
      if (bg && bg !== "transparent") d.style.setProperty("background-color", bg);
      for (const side of SIDES) {
        const pad = st.getPropertyValue(`padding-${side}`);
        if (pad && pad !== "0px") d.style.setProperty(`padding-${side}`, pad);
        const bStyle = st.getPropertyValue(`border-${side}-style`);
        const bWidth = st.getPropertyValue(`border-${side}-width`);
        if (bStyle && bStyle !== "none" && px(bWidth) > 0) {
          d.style.setProperty(
            `border-${side}`,
            `${bWidth} ${bStyle} ${formatValue("color", st.getPropertyValue(`border-${side}-color`))}`
          );
        }
      }
      for (const side of ["top", "bottom"]) {
        const m = st.getPropertyValue(`margin-${side}`);
        if (m && m !== "0px") d.style.setProperty(`margin-${side}`, m);
      }
      if (st.borderTopLeftRadius && st.borderTopLeftRadius !== "0px") d.style.setProperty("border-radius", st.borderTopLeftRadius);
      const deco = st.textDecorationLine;
      if (deco && deco !== "none" && (!parent || parent.textDecorationLine !== deco)) d.style.setProperty("text-decoration", deco);

      if (tag === "UL" || tag === "OL") d.style.setProperty("list-style-type", st.listStyleType);
      if (tag === "IMG") {
        d.style.setProperty("display", st.display);
        d.style.setProperty("max-width", "100%");
        if (!d.getAttribute("height")) d.style.setProperty("height", "auto");
      }
      if (tag === "TABLE") d.style.setProperty("max-width", "100%");
      if (tag === "DIV" && st.display === "inline-block" && s.parentElement) {
        const parentWidth = s.parentElement.getBoundingClientRect().width;
        if (parentWidth > 0) {
          d.style.setProperty("width", `${Math.round((s.getBoundingClientRect().width / parentWidth) * 100)}%`);
        }
      }
    });

    drop.forEach((n) => n.remove());
    return clone;
  };

  const TABLE_OPEN = '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">';
  /** Table parts aren't valid on their own — wrap them in a table. */
  const standalone = (clone: Element) => {
    const t = tagOf(clone);
    if (t === "TD" || t === "TH") return `${TABLE_OPEN}<tr>${clone.outerHTML}</tr></table>`;
    if (t === "TR" || t === "TBODY" || t === "THEAD" || t === "TFOOT") return `${TABLE_OPEN}${clone.outerHTML}</table>`;
    return clone.outerHTML;
  };

  /* ---------------------------- classifying -------------------------- */

  /** A lone link with its own background (or border) that's not a full-width bar. */
  const asButton = (el: Element): { a: HTMLAnchorElement; bg: string } | null => {
    const links = tagOf(el) === "A" ? [el as HTMLAnchorElement] : Array.from(el.querySelectorAll("a"));
    if (links.length !== 1) return null;
    const a = links[0];
    const label = textOf(el);
    if (!label || label.length > 60 || label !== textOf(a) || visibleImgs(el).length) return null;
    const behind = effectiveBg(el.parentElement);
    for (let n: Element | null = a; n; n = n.parentElement) {
      const bg = ownBg(n);
      if (bg !== "transparent" && bg !== behind) {
        return n.getBoundingClientRect().width <= cardWidth * 0.8 ? { a, bg } : null;
      }
      if (n === el) break;
    }
    const s = cs(a);
    if (s.borderTopStyle !== "none" && px(s.borderTopWidth) >= 1 && a.getBoundingClientRect().width <= cardWidth * 0.8) {
      return { a, bg: "transparent" };
    }
    return null;
  };

  const isRule = (el: Element) => {
    if (tagOf(el) === "HR") return true;
    const r = el.getBoundingClientRect();
    if (r.height === 0 || r.height > 4 || r.width < cardWidth * 0.3) return false;
    const s = cs(el);
    return (
      (s.borderTopStyle !== "none" && px(s.borderTopWidth) > 0) ||
      (ownBg(el) !== "transparent" && ownBg(el) !== effectiveBg(el.parentElement))
    );
  };

  /** Pure copy: text, inline formatting, lists, small inline icons — no layout. */
  const isTextLeaf = (el: Element) => {
    if (!hasText(el)) return false;
    if (el.querySelector(STRUCTURAL)) return false;
    if (visibleImgs(el).some(isBigImg)) return false;
    if (hasSideBySideKids(el)) return false;
    return !Array.from(el.querySelectorAll("a")).some((a) => asButton(a));
  };

  /** Flex / grid / float layouts: block children sitting next to each other are columns, not copy. */
  const hasSideBySideKids = (el: Element) => {
    const kids = Array.from(el.children).filter((k) => !isHidden(k) && !cs(k).display.startsWith("inline") && hasContent(k));
    for (let i = 1; i < kids.length; i++) {
      if (sameRow(kids[i - 1].getBoundingClientRect(), kids[i].getBoundingClientRect())) return true;
    }
    return false;
  };

  const isInlineChild = (el: Element) => {
    const d = cs(el).display;
    if (!d.startsWith("inline") || d === "inline-table") return false;
    if (tagOf(el) === "IMG") return !isBigImg(el as HTMLImageElement);
    if (el.querySelector(STRUCTURAL)) return false;
    if (visibleImgs(el).some(isBigImg)) return false;
    return !asButton(el);
  };

  const sameRow = (a: Box, b: Box) => {
    const overlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    const minH = Math.min(a.height, b.height);
    const disjoint = b.left >= a.right - 2 || b.right <= a.left + 2;
    return minH > 0 && overlap > minH * 0.5 && disjoint;
  };

  const unionBox = (boxes: Box[]): Box => {
    const top = Math.min(...boxes.map((b) => b.top));
    const left = Math.min(...boxes.map((b) => b.left));
    const right = Math.max(...boxes.map((b) => b.right));
    const bottom = Math.max(...boxes.map((b) => b.bottom));
    return { top, left, right, bottom, width: right - left, height: bottom - top };
  };

  const runBox = (nodes: Node[]): Box | null => {
    try {
      const range = doc.createRange();
      range.setStartBefore(nodes[0]);
      range.setEndAfter(nodes[nodes.length - 1]);
      const r = range.getBoundingClientRect();
      return r.width || r.height ? r : null;
    } catch {
      return null;
    }
  };

  /* ------------------------------ emitting --------------------------- */

  type Sink = { out: Block[]; measure: boolean; prevBottom: number };
  const newSink = (measure: boolean): Sink => ({ out: [], measure, prevBottom: cardRect.top });

  const wrapParagraph = (html = "") => (/^\s*<(p|div|ul|ol|h\d|blockquote|table|pre)\b/i.test(html) ? html : `<p>${html}</p>`);
  const sameTextStyle = (a: BlockContent, b: BlockContent) =>
    (["textColor", "fontSize", "fontWeight", "fontFamily", "lineHeight", "align", "backgroundColor", "paddingX"] as const).every(
      (k) => a[k] === b[k]
    );

  /**
   * Adds a block. `bg` decides the block's background (only kept when it differs from the card),
   * `pad` is the element whose padding counts as the block's inner spacing.
   */
  const emit = (
    sink: Sink,
    type: BlockType,
    content: BlockContent,
    rect: Box | null,
    opts: { bg?: Element | null; pad?: Element | null } = {}
  ): BlockContent | null => {
    const c: BlockContent = { ...defaultContent(type), ...content };
    if (type !== "html" || opts.bg) {
      const bg = opts.bg ? effectiveBg(opts.bg) : cardBg;
      c.backgroundColor = bg !== cardBg ? bg : "transparent";
    }

    let gap = 0;
    if (sink.measure && rect) {
      gap = Math.max(0, rect.top - sink.prevBottom);
      const ps = opts.pad ? cs(opts.pad) : null;
      const left = rect.left + (ps ? px(ps.paddingLeft) + px(ps.borderLeftWidth) : 0);
      c.paddingX = clamp(Math.round(left - cardLeft), 0, 64);
      c.paddingY =
        ps && c.backgroundColor !== "transparent"
          ? clamp(Math.round(px(ps.paddingTop)), 0, 64)
          : clamp(Math.round(gap / 2), 4, 32);
      sink.prevBottom = Math.max(sink.prevBottom, rect.bottom);
    }

    const prev = sink.out[sink.out.length - 1];
    if (type === "text" && prev?.type === "text" && gap < 28 && sameTextStyle(prev.content, c)) {
      prev.content = { ...prev.content, body: wrapParagraph(prev.content.body) + wrapParagraph(c.body) };
      return null;
    }
    sink.out.push({ id: nextId(), type, content: c });
    return c;
  };

  const emitHtml = (sink: Sink, el: Element) => {
    emit(sink, "html", { html: standalone(inlineStyles(el, "full")) }, el.getBoundingClientRect(), { bg: el.parentElement });
  };

  const emitImage = (sink: Sink, img: HTMLImageElement, box: Element) => {
    const frame = box === img ? img.parentElement ?? img : box;
    const r = img.getBoundingClientRect();
    const { w, h } = imgSize(img);
    const link = img.closest("a");
    const href = link && (link === box || box.contains(link)) ? link.getAttribute("href") ?? "" : "";
    const c = emit(
      sink,
      "image",
      {
        src: img.getAttribute("src") ?? "",
        alt: img.getAttribute("alt") ?? "",
        autoHeight: true,
        imageHeight: clamp(Math.round(h || 260), 80, 800),
        imageFit: "cover",
        imageRadius: Math.round(px(cs(img).borderTopLeftRadius)),
        imageLinkHref: href,
      },
      frame.getBoundingClientRect(),
      { bg: frame, pad: frame }
    );
    if (c) {
      const width = r.width || w;
      const available = cardWidth - 2 * (c.paddingX ?? 0);
      c.widthPct = width && available > 0 ? clamp(Math.round((width / available) * 100), 20, 100) : 100;
    }
  };

  const emitButton = (sink: Sink, btn: { a: HTMLAnchorElement; bg: string }, box: Element) => {
    const ar = btn.a.getBoundingClientRect();
    const br = box.getBoundingClientRect();
    const mid = (ar.left + ar.right) / 2;
    const boxMid = (br.left + br.right) / 2;
    const align: Align =
      Math.abs(mid - boxMid) < 12 ? "center" : ar.left - br.left <= br.right - ar.right ? "left" : "right";
    emit(
      sink,
      "button",
      {
        label: inlineStyles(btn.a, "skip").innerHTML.trim() || escHtml(textOf(btn.a)),
        href: btn.a.getAttribute("href") ?? "#",
        buttonBg: btn.bg === "transparent" ? effectiveBg(box) : btn.bg,
        buttonTextColor: cssColorToHex(cs(btn.a).color) ?? "#ffffff",
        align,
      },
      br,
      { bg: box.parentElement, pad: box }
    );
  };

  const emitHeader = (sink: Sink, el: Element) => {
    emit(
      sink,
      "header",
      { ...typography(cs(el)), kicker: "", title: inlineStyles(el, "skip").innerHTML.trim() },
      el.getBoundingClientRect(),
      { bg: el, pad: el }
    );
  };

  const emitText = (sink: Sink, el: Element) => {
    // Elements whose tag carries meaning (a link, a list, a quote) keep it; others contribute their contents.
    const keepOuter = KEEP_OUTER_TAGS.has(tagOf(el));
    const html = keepOuter ? inlineStyles(el, "diff").outerHTML : inlineStyles(el, "skip").innerHTML;
    const body = normalizeBodyHtml(html.trim());
    if (!body) return;
    // A kept element brings its own padding inline, so don't count it twice.
    emit(sink, "text", { ...typography(cs(el)), body }, el.getBoundingClientRect(), { bg: el, pad: keepOuter ? null : el });
  };

  const emitRun = (sink: Sink, nodes: Node[], container: Element) => {
    const meaningful = nodes.some((n) =>
      n.nodeType === 3 ? !!normText(n.textContent) : tagOf(n as Element) === "IMG" || hasContent(n as Element)
    );
    if (!meaningful) return;
    const html = nodes
      .map((n) => (n.nodeType === 3 ? escHtml(n.textContent ?? "") : inlineStyles(n as Element, "diff").outerHTML))
      .join("")
      .replace(EDGE_BREAKS, "");
    const body = normalizeBodyHtml(`<p>${html}</p>`);
    if (!body) return;
    emit(sink, "text", { ...typography(cs(container)), body }, runBox(nodes) ?? container.getBoundingClientRect(), {
      bg: container,
    });
  };

  /** Folds a column into the columns block's fields — or null if it holds more than they can express. */
  const foldColumn = (el: Element): ColumnContent | null => {
    const sink = newSink(false);
    walk(sink, el);
    if (!sink.out.length) return null;
    const rank: Partial<Record<BlockType, number>> = { image: 0, header: 1, text: 2, button: 3 };
    const s = cs(el);
    const col: ColumnContent = {
      align: alignOf(s),
      textColor: cssColorToHex(s.color) ?? "#111827",
      backgroundColor: ownBg(el) !== effectiveBg(el.parentElement) ? ownBg(el) : "transparent",
    };
    const bodies: string[] = [];
    let last = -1;
    for (const b of sink.out) {
      const r = rank[b.type];
      if (r === undefined || r < last || (r === last && r !== 2)) return null;
      last = r;
      const c = b.content;
      if (b.type === "image") {
        col.image = c.src;
        col.imageAlt = c.alt || undefined;
        col.imageHref = c.imageLinkHref || undefined;
      } else if (b.type === "header") {
        col.title = c.title;
        col.textColor = c.textColor ?? col.textColor;
      } else if (b.type === "text") {
        bodies.push(c.body ?? "");
        if (!col.title) col.textColor = c.textColor ?? col.textColor;
      } else if (b.type === "button") {
        col.buttonLabel = c.label;
        col.buttonHref = c.href;
      }
    }
    if (bodies.length) col.body = bodies.length === 1 ? bodies[0] : bodies.map((b) => wrapParagraph(b)).join("");
    return col;
  };

  /** Side-by-side content that isn't a clean two-column pair: rebuilt as an email-safe table. */
  const rowHtml = (els: Element[]) => {
    const boxes = els.map((e) => e.getBoundingClientRect());
    const total = unionBox(boxes).width || 1;
    const pct = (w: number) => `${Math.max(1, Math.round((w / total) * 100))}%`;
    const cells: string[] = [];
    els.forEach((e, i) => {
      if (i > 0) {
        const gap = boxes[i].left - boxes[i - 1].right;
        if (gap > 2) cells.push(`<td width="${pct(gap)}" style="font-size:0;line-height:0;">&nbsp;</td>`);
      }
      const t = tagOf(e);
      if (t === "TD" || t === "TH") {
        const clone = inlineStyles(e, "full");
        clone.setAttribute("width", pct(boxes[i].width));
        if (!clone.getAttribute("valign")) clone.setAttribute("valign", "top");
        cells.push(clone.outerHTML);
      } else {
        cells.push(`<td width="${pct(boxes[i].width)}" valign="top">${standalone(inlineStyles(e, "full"))}</td>`);
      }
    });
    return `${TABLE_OPEN}<tr>${cells.join("")}</tr></table>`;
  };

  const emitColumns = (sink: Sink, els: Element[]) => {
    const boxes = els.map((e) => e.getBoundingClientRect());
    const union = unionBox(boxes);
    if (els.length === 2) {
      const left = foldColumn(els[0]);
      const right = left && foldColumn(els[1]);
      if (left && right) {
        emit(
          sink,
          "columns",
          { columns: [left, right], columnGap: clamp(Math.round(boxes[1].left - boxes[0].right), 0, 64), align: "left" },
          union,
          { bg: els[0].parentElement }
        );
        return;
      }
    }
    emit(sink, "html", { html: rowHtml(els) }, union, { bg: els[0].parentElement });
  };

  /* ------------------------------- walking --------------------------- */

  type Item = { el: Element; rect: Box } | { nodes: Node[] };

  const walkContainer = (sink: Sink, el: Element) => {
    const items: Item[] = [];
    let run: Node[] = [];
    // Whitespace-only runs (indentation between <td>s, <div>s…) mustn't split a row of columns.
    const flush = () => {
      if (run.some((n) => n.nodeType !== 3 || !!normText(n.textContent))) items.push({ nodes: run });
      run = [];
    };

    el.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        run.push(node);
        return;
      }
      if (node.nodeType !== 1) return;
      const child = node as Element;
      const tag = tagOf(child);
      if (tag === "STYLE" || tag === "SCRIPT" || tag === "TEMPLATE") return;
      if (isHidden(child)) return;
      if (tag === "BR" || isInlineChild(child)) {
        run.push(child);
        return;
      }
      flush();
      if (hasContent(child) || isRule(child)) items.push({ el: child, rect: child.getBoundingClientRect() });
    });
    flush();

    // Group into visual rows: elements that sit next to each other are columns.
    const rows: Item[][] = [];
    for (const item of items) {
      const row = rows[rows.length - 1];
      const prev = row?.[row.length - 1];
      if (prev && "el" in prev && "el" in item && sameRow(prev.rect, item.rect)) row.push(item);
      else rows.push([item]);
    }

    for (const row of rows) {
      if (row.length === 1) {
        const it = row[0];
        if ("nodes" in it) emitRun(sink, it.nodes, el);
        else walk(sink, it.el);
        continue;
      }
      const els = row.flatMap((it) => ("el" in it && hasContent(it.el) ? [it.el] : []));
      if (els.length === 1) walk(sink, els[0]);
      else if (els.length > 1) emitColumns(sink, els);
    }
  };

  function walk(sink: Sink, el: Element): void {
    const tag = tagOf(el);
    if (tag === "STYLE" || tag === "HEAD" || tag === "TITLE" || tag === "BR") return;
    if (isHidden(el)) return;
    if (!hasContent(el)) {
      if (isRule(el)) emit(sink, "divider", {}, el.getBoundingClientRect(), { bg: el.parentElement });
      return;
    }
    if (RAW_TAGS.has(tag)) return emitHtml(sink, el);
    if (tag === "IMG") {
      if (!isSpacerImg(el as HTMLImageElement)) emitImage(sink, el as HTMLImageElement, el);
      return;
    }

    const imgs = visibleImgs(el);
    if (!hasText(el) && imgs.length === 1 && isBigImg(imgs[0]) && !el.querySelector("svg, video, canvas")) {
      return emitImage(sink, imgs[0], el);
    }
    const btn = asButton(el);
    if (btn) return emitButton(sink, btn, el);
    if (HEADING_TAGS.test(tag)) return emitHeader(sink, el);

    // Pure wrappers (table parts, or an element whose only content is one child)
    // are skipped so styles are read from the element that actually holds the copy.
    const hasDirectText = Array.from(el.childNodes).some((n) => n.nodeType === 3 && !!normText(n.textContent));
    const kids = Array.from(el.children).filter((k) => !isHidden(k) && hasContent(k));
    if (!hasDirectText && kids.length === 1 && !isInlineChild(kids[0])) return walk(sink, kids[0]);

    if (!LAYOUT_TAGS.has(tag) && isTextLeaf(el)) return emitText(sink, el);
    walkContainer(sink, el);
  }

  /* ------------------------------- result ---------------------------- */

  const main = newSink(true);
  walk(main, card);
  if (!main.out.length) return null;

  const theme: Theme = { ...DEFAULT_THEME, pageBg, cardBg, cardRadius, cardWidth, pagePadding };
  const section: Block = {
    id: nextId(),
    type: "section",
    content: {
      ...defaultContent("section"),
      sectionTitle: "Imported",
      backgroundColor: "transparent",
      paddingY: pagePadding,
      innerBg: cardBg,
      innerRadius: cardRadius,
      innerWidth: cardWidth,
    },
  };
  return { blocks: [section, ...main.out], theme };
}

function importForeignHtml(html: string): ImportResult | null {
  return withRenderedDocument(html, IMPORT_VIEWPORT, (doc, win) => convertRenderedDocument(doc, win));
}

/** Last resort (no DOM layout available): keep the whole body as one HTML block. */
function rawHtmlDoc(html: string): Doc {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  return {
    theme: DEFAULT_THEME,
    blocks: [
      { id: nextId(), type: "section", content: { ...defaultContent("section"), sectionTitle: "Imported" } },
      {
        id: nextId(),
        type: "html",
        content: { ...defaultContent("html"), paddingX: 0, paddingY: 0, html: sanitizeFragment(parsed.body?.innerHTML ?? "") },
      },
    ],
  };
}

function parseHtml(html?: string): Doc | null {
  const normalizedHtml = cleanImportedHtml(html);

  if (!normalizedHtml?.trim()) {
    return { blocks: getDefaultBlocks(), theme: DEFAULT_THEME };
  }

  // Anything not produced by this builder goes through the layout-aware importer.
  if (!isBuilderHtml(normalizedHtml)) {
    try {
      return importForeignHtml(normalizedHtml) ?? rawHtmlDoc(normalizedHtml);
    } catch {
      return null;
    }
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(normalizedHtml, "text/html");
    const outerTable = doc.querySelector('table[role="presentation"]');
    const outerStyle = parseStyle(outerTable?.getAttribute("style"));

    const theme: Theme = { ...DEFAULT_THEME, pageBg: outerStyle.background || DEFAULT_THEME.pageBg };

    const sectionTds = outerTable
      ? Array.from(outerTable.querySelectorAll(":scope > tbody > tr > td, :scope > tr > td"))
      : [];
    const blocks: Block[] = [];

    const push = (type: BlockType, content: BlockContent) => {
      blocks.push({ id: nextId(), type, content });
    };

    const parseBlockTd = (td: HTMLTableCellElement) => {
      const blockType = td.getAttribute("data-block-type") as BlockType | null;
      const tdStyle = parseStyle(td.getAttribute("style"));
      const padParts = tdStyle.padding?.split(" ");

      const common = {
        align: (tdStyle["text-align"] as Align) || "left",
        backgroundColor: tdStyle.background && tdStyle.background !== "transparent" ? tdStyle.background : undefined,
        wrapperRadius: parsePx(tdStyle["border-radius"]) ?? 0,
        paddingY: parsePx(padParts?.[0]),
        paddingX: parsePx(padParts?.[1] || padParts?.[0]),
      };

      if (blockType === "header") {
        const kicker = td.querySelector('[data-field="kicker"]');
        const title = td.querySelector('[data-field="title"]');
        const titleStyle = parseStyle(title?.getAttribute("style"));
        push("header", {
          ...defaultContent("header"),
          ...common,
          kicker: kicker?.innerHTML.trim() || "",
          title: title?.innerHTML.trim() || "",
          textColor: titleStyle.color,
          fontSize: parsePx(titleStyle["font-size"]),
          fontWeight: (parseNumeric(titleStyle["font-weight"]) as BlockContent["fontWeight"]) || 700,
          fontFamily: titleStyle["font-family"],
          lineHeight: parseNumeric(titleStyle["line-height"]),
          underline: titleStyle["text-decoration"]?.includes("underline"),
        });
        return;
      }

      if (blockType === "text") {
        const bodyNode = td.querySelector('[data-field="body"]') as HTMLElement | null;
        const link = td.querySelector('[data-field="link"] a');
        const bodyStyle = parseStyle(bodyNode?.getAttribute("style"));
        const fallbackBodyHtml = Array.from(td.childNodes)
          .filter((node) => {
            if (node.nodeType === Node.TEXT_NODE) return (node.textContent || "").trim().length > 0;
            if (!(node instanceof HTMLElement)) return false;
            return node.getAttribute("data-field") !== "link";
          })
          .map((node) => (node.nodeType === Node.TEXT_NODE ? node.textContent || "" : (node as HTMLElement).outerHTML))
          .join("")
          .trim();
        const bodyHtml = (bodyNode?.innerHTML || "").trim() || fallbackBodyHtml;

        push("text", {
          ...defaultContent("text"),
          ...common,
          body: normalizeBodyHtml(bodyHtml),
          textColor: bodyStyle.color,
          fontSize: parsePx(bodyStyle["font-size"]),
          fontWeight: (parseNumeric(bodyStyle["font-weight"]) as BlockContent["fontWeight"]) || 400,
          fontFamily: bodyStyle["font-family"],
          lineHeight: parseNumeric(bodyStyle["line-height"]),
          underline: bodyStyle["text-decoration"]?.includes("underline"),
          linkLabel: link?.innerHTML.trim() || "",
          linkHref: stripTickWrappedUrl(link?.getAttribute("href")),
        });
        return;
      }

      if (blockType === "image") {
        const img = td.querySelector("img");
        const imageLink = td.querySelector('[data-field="image-link"]');
        const imgStyle = parseStyle(img?.getAttribute("style"));
        const widthStyle = imgStyle.width?.replace("%", "");
        const rawHeight = img?.getAttribute("data-height");
        const autoHeight = rawHeight === "auto" || imgStyle.height === "auto";

        push("image", {
          ...defaultContent("image"),
          ...common,
          src: stripTickWrappedUrl(img?.getAttribute("src")) || "",
          alt: img?.getAttribute("alt") || "",
          widthPct: clamp(Number(td.getAttribute("data-width-pct") || widthStyle || 100), 20, 100),
          imageLinkHref: stripTickWrappedUrl(imageLink?.getAttribute("href")),
          autoHeight,
          imageHeight: autoHeight ? 260 : clamp(Number(rawHeight || parsePx(imgStyle.height) || 260), 80, 800),
          imageRadius: parseNumeric(img?.getAttribute("data-radius")) ?? parsePx(imgStyle["border-radius"]) ?? 10,
          imageFit: (img?.getAttribute("data-fit") as "cover" | "contain") || "cover",
        });
        return;
      }

      if (blockType === "columns") {
        const gap = parseNumeric(td.getAttribute("data-column-gap")) ?? 24;
        const colTds = Array.from(td.querySelectorAll("[data-col]"));

        const parseColumn = (index: number): ColumnContent => {
          const colTd = colTds.find((el) => el.getAttribute("data-col") === String(index)) as HTMLElement | undefined;
          if (!colTd) return defaultColumn(index === 0 ? "Column one" : "Column two");

          const colStyle = parseStyle(colTd.getAttribute("style"));
          const img = colTd.querySelector(`[data-field="col-${index}-image"]`) as HTMLImageElement | null;
          const title = colTd.querySelector(`[data-field="col-${index}-title"]`) as HTMLElement | null;
          const body = colTd.querySelector(`[data-field="col-${index}-body"]`) as HTMLElement | null;
          const button = colTd.querySelector(`[data-field="col-${index}-button"]`) as HTMLElement | null;
          const titleStyle = parseStyle(title?.getAttribute("style"));

          return {
            align: (colStyle["text-align"] as Align) || "left",
            backgroundColor:
              colStyle.background && colStyle.background !== "transparent" ? colStyle.background : "transparent",
            image: stripTickWrappedUrl(img?.getAttribute("src")) || undefined,
            imageAlt: img?.getAttribute("alt") || undefined,
            imageHref: stripTickWrappedUrl(img?.closest("a")?.getAttribute("href")) || undefined,
            title: title?.innerHTML.trim() || "",
            body: (body?.innerHTML || "").trim(),
            textColor: titleStyle.color || "#111827",
            buttonLabel: button?.innerHTML.trim() || undefined,
            buttonHref: stripTickWrappedUrl(button?.getAttribute("href")) || undefined,
          };
        };

        push("columns", {
          ...defaultContent("columns"),
          ...common,
          columnGap: gap,
          columns: [parseColumn(0), parseColumn(1)],
        });
        return;
      }

      if (blockType === "button") {
        const link = td.querySelector("a");
        const linkStyle = parseStyle(link?.getAttribute("style"));
        push("button", {
          ...defaultContent("button"),
          ...common,
          label: link?.innerHTML.trim() || "",
          href: stripTickWrappedUrl(link?.getAttribute("href")),
          buttonBg: linkStyle.background,
          buttonTextColor: linkStyle.color,
        });
        return;
      }

      if (blockType === "divider") {
        push("divider", { ...defaultContent("divider"), ...common });
        return;
      }

      if (blockType === "html") {
        push("html", { ...defaultContent("html"), ...common, html: sanitizeFragment(td.innerHTML) });
        return;
      }

      if (blockType === "footer") {
        push("footer", {
          ...defaultContent("footer"),
          ...common,
          address: td.querySelector('[data-field="address"]')?.innerHTML.trim() || "",
          unsub: td.querySelector('[data-field="unsub"]')?.innerHTML.trim() || "",
        });
      }
    };

    sectionTds.forEach((sectionTd, index) => {
      const sectionStyle = parseStyle(sectionTd.getAttribute("style"));
      const inner = sectionTd.querySelector('table[role="presentation"]') as HTMLTableElement | null;
      const innerStyle = parseStyle(inner?.getAttribute("style"));

      const innerMaxWidth = parsePx(innerStyle["max-width"]);
      const innerWidth = innerMaxWidth || parseNumeric(inner?.getAttribute("width") || "") || DEFAULT_THEME.cardWidth;

      push("section", {
        ...defaultContent("section"),
        sectionTitle: sectionTd.getAttribute("data-section-title") || `Section ${index + 1}`,
        backgroundColor:
          sectionStyle.background && sectionStyle.background !== "transparent" ? sectionStyle.background : "transparent",
        paddingY: parsePx(sectionStyle.padding?.split(" ")[0]) ?? DEFAULT_THEME.pagePadding,
        paddingX: 16,
        innerBg: innerStyle.background || DEFAULT_THEME.cardBg,
        innerRadius: parsePx(innerStyle["border-radius"]) ?? DEFAULT_THEME.cardRadius,
        innerWidth,
      });

      const innerRows = inner ? Array.from(inner.querySelectorAll(":scope > tbody > tr, :scope > tr")) : [];
      innerRows.forEach((row) => {
        const td = row.querySelector("td") as HTMLTableCellElement | null;
        if (td) parseBlockTd(td);
      });
    });

    return { blocks: blocks.length ? blocks : getDefaultBlocks(), theme };
  } catch {
    return null;
  }
}

const docFromHtml = (html?: string): Doc => parseHtml(html) ?? { blocks: getDefaultBlocks(), theme: DEFAULT_THEME };

/* ================================================================== */
/*  Hooks                                                             */
/* ================================================================== */

type HistoryState = { past: Doc[]; present: Doc; future: Doc[] };

/** Undo/redo stack. Rapid edits sharing a coalesce key (typing, slider drags) collapse into one step. */
function useDocHistory(initial: Doc) {
  const [state, setState] = useState<HistoryState>({ past: [], present: initial, future: [] });
  const lastKey = useRef<{ key: string; at: number } | null>(null);

  const apply = useCallback((updater: (doc: Doc) => Doc, coalesceKey?: string) => {
    const now = Date.now();
    const prev = lastKey.current;
    const coalesce = !!coalesceKey && !!prev && prev.key === coalesceKey && now - prev.at < COALESCE_MS;
    lastKey.current = coalesceKey ? { key: coalesceKey, at: now } : null;

    setState((s) => {
      const next = updater(s.present);
      if (next === s.present) return s;
      return {
        past: coalesce ? s.past : [...s.past, s.present].slice(-HISTORY_LIMIT),
        present: next,
        future: [],
      };
    });
  }, []);

  const undo = useCallback(() => {
    lastKey.current = null;
    setState((s) =>
      s.past.length
        ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] }
        : s
    );
  }, []);

  const redo = useCallback(() => {
    lastKey.current = null;
    setState((s) =>
      s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s
    );
  }, []);

  const reset = useCallback((doc: Doc) => {
    lastKey.current = null;
    setState({ past: [], present: doc, future: [] });
  }, []);

  return {
    doc: state.present,
    apply,
    undo,
    redo,
    reset,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}

function useElementWidth(ref: RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(0);
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

function useOutsideClick(refs: RefObject<HTMLElement | null>[], active: boolean, onOutside: () => void) {
  const cb = useRef(onOutside);
  cb.current = onOutside;
  useEffect(() => {
    if (!active) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (refs.some((r) => r.current?.contains(target))) return;
      cb.current();
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/**
 * Remembers the last selection inside any rich-text field, so toolbar controls
 * that steal focus (selects, colour inputs, prompts) can still act on it.
 */
function useSavedRteRange() {
  const saved = useRef<{ range: Range; target: HTMLElement } | null>(null);

  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const el = (node instanceof HTMLElement ? node : node.parentElement)?.closest<HTMLElement>("[data-rte]");
      if (el) saved.current = { range: range.cloneRange(), target: el };
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  return useCallback((requireFocused: boolean) => {
    const s = saved.current;
    if (!s || !s.target.isConnected || !s.target.contains(s.range.commonAncestorContainer)) return null;
    if (requireFocused && document.activeElement !== s.target) return null;
    s.target.focus({ preventScroll: true });
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(s.range);
    return s;
  }, []);
}

async function resolveImageFile(file: File, onUploadImage?: UploadFn): Promise<string | null> {
  if (onUploadImage) return onUploadImage(file);
  if (
    file.size > MAX_EMBED_BYTES &&
    !window.confirm(
      `This image is ${Math.round(file.size / 1024)} KB. Embedded images make emails heavy and are blocked by some inboxes (e.g. Gmail). Embed anyway?`
    )
  ) {
    return null;
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/* ================================================================== */
/*  Primitives                                                        */
/* ================================================================== */

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-3">
      <span className="label">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Group({
  title,
  hint,
  defaultOpen = true,
  children,
}: {
  title: string;
  hint?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details open={defaultOpen} className="group border-b py-3 last:border-b-0">
      <summary className="flex cursor-pointer list-none items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronDown size={13} className="transition-transform group-open:rotate-180" />
      </summary>
      {hint && <p className="mt-1.5 text-[11.5px] text-muted-foreground">{hint}</p>}
      <div className="mt-3">{children}</div>
    </details>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  dark,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  dark?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cx(
        "inline-flex items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-30",
        dark ? "h-6 w-6 bg-gray-900 text-white hover:bg-gray-700" : "h-8 w-8 text-muted-foreground hover:bg-gray-100",
        danger && (dark ? "hover:bg-red-600" : "text-red-500 hover:bg-red-50"),
        className
      )}
    >
      {children}
    </button>
  );
}

const chipClass =
  "inline-flex shrink-0 items-center gap-1 rounded-[8px] border bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:border-gray-900";

function ChipButton({ onClick, children, title }: { onClick: () => void; children: ReactNode; title?: string }) {
  return (
    <button
      type="button"
      title={title}
      className={chipClass}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

function ColorPicker({
  value,
  onChange,
  allowTransparent,
}: {
  value?: string;
  onChange: (v: string) => void;
  allowTransparent?: boolean;
}) {
  const isNone = !value || value === "transparent";
  const swatch = "h-7 w-7 rounded-full border lg:h-6 lg:w-6";
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {allowTransparent && (
        <button
          type="button"
          aria-label="No colour"
          title="None"
          onClick={() => onChange("transparent")}
          className={cx(swatch, "relative overflow-hidden bg-white", isNone && "ring-2 ring-gray-900 ring-offset-1")}
        >
          <span className="absolute left-1/2 top-[-4px] h-9 w-px -translate-x-1/2 rotate-45 bg-red-500" />
        </button>
      )}
      {COLOR_PALETTE.map((color) => (
        <button
          key={color}
          type="button"
          aria-label={color}
          title={color}
          className={cx(swatch, value?.toLowerCase() === color && "ring-2 ring-gray-900 ring-offset-1")}
          style={{ background: color }}
          onClick={() => onChange(color)}
        />
      ))}
      <label
        className={cx(swatch, "relative cursor-pointer overflow-hidden")}
        title="Custom colour"
        style={{ background: "conic-gradient(#ef4444, #eab308, #22c55e, #06b6d4, #3b82f6, #ec4899, #ef4444)" }}
      >
        <input
          type="color"
          aria-label="Custom colour"
          value={value && HEX6.test(value) ? value : "#111827"}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </label>
      {value && !isNone && <span className="ml-1 font-mono text-[11px] text-muted-foreground">{value}</span>}
    </div>
  );
}

function RangeField({
  label,
  value,
  min,
  max,
  unit = "px",
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  onChange: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        <input
          type="range"
          aria-label={label}
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-6 min-w-0 flex-1 accent-gray-900"
        />
        <div className="flex shrink-0 items-center rounded-md border bg-white px-1.5">
          <input
            type="number"
            inputMode="numeric"
            aria-label={`${label} value`}
            min={min}
            max={max}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              const n = Number(e.target.value);
              if (e.target.value !== "" && !Number.isNaN(n) && n >= min && n <= max) onChange(n);
            }}
            onBlur={() => {
              const n = Number(draft);
              if (draft !== "" && !Number.isNaN(n)) onChange(clamp(n, min, max));
              setDraft(String(value));
            }}
            className="w-11 bg-transparent py-1 text-right text-[12px] outline-none"
          />
          <span className="pl-0.5 text-[11px] text-muted-foreground">{unit}</span>
        </div>
      </div>
    </Field>
  );
}

function NumberSelect({ value, onChange, options }: { value?: number; onChange: (v: number) => void; options: number[] }) {
  const all = value !== undefined && !options.includes(value) ? [...options, value].sort((a, b) => a - b) : options;
  return (
    <select className="input" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}>
      <option value="" disabled>
        Select…
      </option>
      {all.map((option) => (
        <option key={option} value={option}>
          {option}px
        </option>
      ))}
    </select>
  );
}

function WeightSelect({
  value,
  onChange,
}: {
  value?: BlockContent["fontWeight"];
  onChange: (v: BlockContent["fontWeight"]) => void;
}) {
  const labels: Record<number, string> = { 400: "Regular", 500: "Medium", 600: "Semibold", 700: "Bold" };
  return (
    <select
      className="input"
      value={value ?? 400}
      onChange={(e) => onChange(Number(e.target.value) as BlockContent["fontWeight"])}
    >
      {[400, 500, 600, 700].map((option) => (
        <option key={option} value={option}>
          {labels[option]}
        </option>
      ))}
    </select>
  );
}

function FontSelect({ value, fallback, onChange }: { value?: string; fallback: string; onChange: (v: string) => void }) {
  const current = value || fallback;
  const known = FONT_OPTIONS.some(([, v]) => v === current);
  return (
    <select className="input" value={current} onChange={(e) => onChange(e.target.value)}>
      {!known && <option value={current}>{current.split(",")[0].replace(/'/g, "")} (imported)</option>}
      {FONT_OPTIONS.map(([label, v]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function AlignPicker({ value, onChange }: { value?: Align; onChange: (v: Align) => void }) {
  const options: { value: Align; Icon: LucideIcon }[] = [
    { value: "left", Icon: AlignLeft },
    { value: "center", Icon: AlignCenter },
    { value: "right", Icon: AlignRight },
  ];
  const current = value ?? "left";
  return (
    <div className="inline-flex gap-1" role="group" aria-label="Alignment">
      {options.map(({ value: option, Icon }) => (
        <button
          key={option}
          type="button"
          aria-label={`Align ${option}`}
          aria-pressed={current === option}
          className={cx(
            "flex h-8 w-8 items-center justify-center rounded-[8px] border",
            current === option ? "border-black bg-black text-white" : "text-muted-foreground hover:bg-gray-50"
          )}
          onClick={() => onChange(option)}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
}

function UrlInput({
  value,
  onChange,
  placeholder = "https://",
}: {
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const v = value ?? "";
  return (
    <>
      <input
        className="input"
        inputMode="url"
        autoComplete="off"
        placeholder={placeholder}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          const normalized = normalizeLinkUrl(e.target.value);
          if (normalized !== e.target.value) onChange(normalized);
        }}
      />
      {v.trim() && !looksLikeUrl(v) && (
        <p className="mt-1 text-[11px] text-amber-600">Add https:// so the link works in every inbox.</p>
      )}
    </>
  );
}

/** Button that expands an inline (in-flow) URL field — never clipped by rounded cards. */
function UrlPopoverButton({
  icon: Icon = Link2,
  label,
  value,
  placeholder = "https://",
  onApply,
}: {
  icon?: LucideIcon;
  label: string;
  value?: string;
  placeholder?: string;
  onApply: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const btnRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  useOutsideClick([btnRef, boxRef], open, () => setOpen(false));

  const commit = () => {
    onApply(normalizeLinkUrl(draft));
    setOpen(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        className={chipClass}
        onClick={(e) => {
          e.stopPropagation();
          setDraft(value ?? "");
          setOpen((o) => !o);
        }}
      >
        <Icon size={12} />
        <span className="max-w-[140px] truncate">{label}</span>
      </button>
      {open && (
        <div ref={boxRef} className="flex basis-full items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <input
            autoFocus
            className="input h-8 min-w-0 flex-1 text-[12px]"
            inputMode="url"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                commit();
              }
              if (e.key === "Escape") {
                e.stopPropagation();
                setOpen(false);
              }
            }}
          />
          <button
            type="button"
            aria-label="Apply"
            onClick={commit}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white"
          >
            <Check size={14} />
          </button>
        </div>
      )}
    </>
  );
}

function ImageUploadButton({
  onUploaded,
  onUploadImage,
  label = "Upload",
}: {
  onUploaded: (url: string) => void;
  onUploadImage?: UploadFn;
  label?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <label className={cx(chipClass, "cursor-pointer", busy && "pointer-events-none opacity-60")} onClick={(e) => e.stopPropagation()}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {busy ? "Uploading…" : label}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            input.value = "";
            if (!file) return;
            setBusy(true);
            setError(null);
            try {
              const url = await resolveImageFile(file, onUploadImage);
              if (url) onUploaded(url);
            } catch {
              setError("Upload failed — try again.");
            } finally {
              setBusy(false);
            }
          }}
        />
      </label>
      {error && <span className="basis-full text-[11px] text-red-600">{error}</span>}
    </>
  );
}

function InsertMenu({ onPick, label = "Add block" }: { onPick: (type: BlockType) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick([ref], open, () => setOpen(false));

  return (
    <div ref={ref} className="flex w-full flex-col items-center" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 bg-white/80 px-3 py-1.5 text-[12px] text-gray-500 hover:border-gray-900 hover:text-gray-900"
      >
        {open ? <X size={13} /> : <Plus size={13} />}
        {open ? "Close" : label}
      </button>
      {open && (
        <div className="mt-2 grid w-full max-w-md grid-cols-2 gap-1 rounded-xl border bg-white p-1.5 shadow-lg sm:grid-cols-4">
          {BLOCK_TYPES.map(({ type, label: typeLabel, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => {
                onPick(type);
                setOpen(false);
              }}
              className="flex items-center gap-2 rounded-lg px-2 py-2 text-left text-[12px] hover:bg-gray-100"
            >
              <Icon size={14} />
              {typeLabel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Rich text                                                         */
/* ================================================================== */

/** Single-line fields: store exactly what the browser produced, minus leading/trailing breaks. */
function normalizeInlineHtml(html: string): string {
  const box = document.createElement("div");
  box.innerHTML = html;
  if (!box.textContent?.trim() && !box.querySelector("img, table")) return "";
  return html.replace(/^(\s|<br\s*\/?>)+|(\s|<br\s*\/?>)+$/gi, "");
}

/**
 * Uncontrolled contentEditable. The DOM is only rewritten when the value changes
 * from outside (undo, panel edits) and the field isn't focused, so the caret
 * never jumps. Edits commit on a short debounce and on blur.
 */
function EditableRichText({
  value = "",
  onCommit,
  plain,
  normalize,
  className,
  style,
  placeholder,
  onUploadImage,
}: {
  value?: string;
  onCommit: (v: string) => void;
  plain?: boolean;
  normalize?: (v: string) => string;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  onUploadImage?: UploadFn;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const commitRef = useRef(onCommit);
  const normalizeRef = useRef(normalize);
  const valueRef = useRef(value);
  commitRef.current = onCommit;
  normalizeRef.current = normalize;
  valueRef.current = value;
  const lastSent = useRef<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value === lastSent.current) {
      lastSent.current = null; // echo of our own commit — DOM is already right
      return;
    }
    if (document.activeElement === el) return;
    if (el.innerHTML !== value) el.innerHTML = value;
  }, [value]);

  const flushFrom = useCallback(
    (el: HTMLDivElement | null) => {
      window.clearTimeout(timer.current);
      timer.current = undefined;
      if (!el) return;
      const raw = el.innerHTML;
      const next = normalizeRef.current ? normalizeRef.current(raw) : plain ? normalizeInlineHtml(raw) : raw;
      if (next === valueRef.current) return;
      lastSent.current = next;
      commitRef.current(next);
    },
    [plain]
  );

  useEffect(() => {
    const el = ref.current;
    return () => {
      if (timer.current !== undefined) flushFrom(el);
    };
  }, [flushFrom]);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline={!plain}
      aria-label={placeholder}
      data-rte={plain ? "inline" : "rich"}
      data-placeholder={placeholder}
      className={cx(
        "cursor-text rounded-sm outline-none focus:ring-2 focus:ring-blue-200",
        "empty:before:pointer-events-none empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)]",
        className
      )}
      style={style}
      onInput={() => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => flushFrom(ref.current), 400);
      }}
      onBlur={() => flushFrom(ref.current)}
      onPaste={(e) => {
        // Formatted content (Docs, Word, web pages, other emails): let the browser paste it
        // natively so every style is kept — same as the original editor.
        const cd = e.clipboardData;
        if (cd.getData("text/html") || cd.getData("text/plain")) return;

        // Only a raw image on the clipboard (screenshot, "Copy image") needs help:
        // browsers can't paste those into an editable div by themselves.
        const imageFile = Array.from(cd.files).find((f) => f.type.startsWith("image/"));
        if (!imageFile) return;
        e.preventDefault();
        const el = ref.current;
        const sel = window.getSelection();
        const range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
        resolveImageFile(imageFile, onUploadImage)
          .then((src) => {
            if (!src || !el) return;
            el.focus({ preventScroll: true });
            if (range && el.contains(range.commonAncestorContainer)) {
              const s = window.getSelection();
              s?.removeAllRanges();
              s?.addRange(range);
            }
            document.execCommand(
              "insertHTML",
              false,
              `<img src="${escAttr(src)}" alt="" style="max-width:100%;height:auto;border:0;" />`
            );
          })
          .catch(() => window.alert("Couldn't add that image — try uploading it instead."));
      }}
    />
  );
}

function RichTextToolbar({ compact }: { compact?: boolean }) {
  const restore = useSavedRteRange();

  const run = (fn: (target: HTMLElement, range: Range) => void, needsSelection = false) => {
    const saved = restore(false);
    if (!saved || !saved.target.dataset.rte) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (needsSelection && range.collapsed) return;
    fn(saved.target, range);
    saved.target.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const exec = (cmd: string) => run(() => document.execCommand(cmd, false));

  const wrapStyle = (prop: string, val: string) =>
    run((_target, range) => {
      const span = document.createElement("span");
      span.style.setProperty(prop, val);
      span.appendChild(range.extractContents());
      range.insertNode(span);
      const next = document.createRange();
      next.selectNodeContents(span);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(next);
    }, true);

  const link = () =>
    run((target, range) => {
      const url = window.prompt("Link URL", "https://");
      if (!url) return;
      target.focus({ preventScroll: true });
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      document.execCommand("createLink", false, normalizeLinkUrl(url));
    }, true);

  const clear = () =>
    run(() => {
      document.execCommand("removeFormat");
      document.execCommand("unlink");
    }, true);

  const btn = "flex h-7 min-w-7 shrink-0 items-center justify-center rounded px-1.5 text-gray-700 hover:bg-gray-100";
  const sep = <span className="mx-0.5 h-5 w-px shrink-0 bg-gray-200" />;

  return (
    <div
      role="toolbar"
      aria-label="Text formatting"
      className={cx(
        "flex items-center gap-0.5 overflow-x-auto bg-white p-1",
        compact ? "border-b" : "rounded-[12px] border shadow-sm"
      )}
      onMouseDown={(e) => {
        // Keep the text selection when clicking toolbar buttons.
        if ((e.target as HTMLElement).closest("button")) e.preventDefault();
      }}
    >
      <button type="button" className={btn} title="Bold" aria-label="Bold" onClick={() => exec("bold")}>
        <Bold size={14} />
      </button>
      <button type="button" className={btn} title="Italic" aria-label="Italic" onClick={() => exec("italic")}>
        <Italic size={14} />
      </button>
      <button type="button" className={btn} title="Underline" aria-label="Underline" onClick={() => exec("underline")}>
        <UnderlineIcon size={14} />
      </button>
      <button type="button" className={btn} title="Add link" aria-label="Add link" onClick={link}>
        <Link2 size={14} />
      </button>
      <button type="button" className={btn} title="Clear formatting" aria-label="Clear formatting" onClick={clear}>
        <Eraser size={14} />
      </button>
      {sep}
      <select
        aria-label="Font size"
        defaultValue=""
        className="h-7 shrink-0 rounded border bg-white px-1 text-[12px]"
        onChange={(e) => {
          const v = e.currentTarget.value;
          e.currentTarget.value = "";
          if (v) wrapStyle("font-size", `${v}px`);
        }}
      >
        <option value="">Size</option>
        {[12, 14, 16, 18, 20, 24, 28, 32].map((size) => (
          <option key={size} value={size}>
            {size}px
          </option>
        ))}
      </select>
      <select
        aria-label="Font weight"
        defaultValue=""
        className="h-7 shrink-0 rounded border bg-white px-1 text-[12px]"
        onChange={(e) => {
          const v = e.currentTarget.value;
          e.currentTarget.value = "";
          if (v) wrapStyle("font-weight", v);
        }}
      >
        <option value="">Weight</option>
        {[400, 500, 600, 700].map((w) => (
          <option key={w} value={w}>
            {w}
          </option>
        ))}
      </select>
      {sep}
      {COLOR_PALETTE.slice(0, compact ? 6 : 10).map((color) => (
        <button
          key={`t-${color}`}
          type="button"
          title={`Text ${color}`}
          aria-label={`Text colour ${color}`}
          className="h-5 w-5 shrink-0 rounded border"
          style={{ background: color }}
          onClick={() => wrapStyle("color", color)}
        />
      ))}
      {sep}
      {HIGHLIGHT_PALETTE.map((color) => (
        <button
          key={`h-${color}`}
          type="button"
          title={`Highlight ${color}`}
          aria-label={`Highlight ${color}`}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[10px] font-bold text-gray-700"
          style={{ background: color }}
          onClick={() => wrapStyle("background-color", color)}
        >
          A
        </button>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Block rendering (shared by builder + inline editing)              */
/* ================================================================== */

type TextOpts = {
  value?: string;
  onCommit: (v: string) => void;
  plain?: boolean;
  normalize?: (v: string) => string;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
};

function BlockView({
  block,
  mobile,
  inline,
  selected,
  onPatch,
  onUploadImage,
}: {
  block: Block;
  mobile: boolean;
  inline: boolean;
  selected: boolean;
  onPatch: PatchFn;
  onUploadImage?: UploadFn;
}) {
  const c = block.content;
  const set: SetFn = (patch, key) => onPatch(block.id, patch, `${block.id}:${key ?? Object.keys(patch).join(",")}`);
  const padX = mobile ? Math.min(c.paddingX ?? 32, 16) : c.paddingX ?? 32;
  const wrapper: CSSProperties = {
    padding: `${c.paddingY ?? 10}px ${padX}px`,
    textAlign: c.align ?? "left",
    background: bgOrUndefined(c.backgroundColor),
    borderRadius: c.wrapperRadius ?? 0,
  };

  // Plain function (not a component) so the editable element keeps a stable identity.
  const text = (o: TextOpts) => {
    if (inline) {
      return (
        <EditableRichText
          value={o.value ?? ""}
          onCommit={o.onCommit}
          plain={o.plain}
          normalize={o.normalize}
          className={o.className}
          style={o.style}
          placeholder={o.placeholder}
          onUploadImage={onUploadImage}
        />
      );
    }
    if (!o.value && o.placeholder) {
      return (
        <div className={o.className} style={o.style}>
          <span className="text-gray-400">{o.placeholder}</span>
        </div>
      );
    }
    return <div className={o.className} style={o.style} dangerouslySetInnerHTML={{ __html: o.value ?? "" }} />;
  };

  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  if (block.type === "header") {
    return (
      <div style={wrapper}>
        {(inline || !!c.kicker) &&
          text({
            value: c.kicker,
            plain: true,
            onCommit: (v) => set({ kicker: v }),
            className: "mb-1.5 uppercase",
            style: { fontFamily: "Arial, sans-serif", fontSize: 11, letterSpacing: 1.5, color: c.textColor ?? "#6b7280" },
            placeholder: "Kicker",
          })}
        {text({
          value: c.title,
          plain: true,
          onCommit: (v) => set({ title: v }),
          style: {
            fontFamily: c.fontFamily || "Georgia, serif",
            fontSize: c.fontSize ?? 26,
            lineHeight: c.lineHeight ?? 1.3,
            color: c.textColor ?? "#111827",
            fontWeight: c.fontWeight ?? 700,
            textDecoration: c.underline ? "underline" : undefined,
          },
          placeholder: "Headline",
        })}
      </div>
    );
  }

  if (block.type === "text") {
    return (
      <div style={wrapper}>
        {text({
          value: c.body,
          normalize: normalizeBodyHtml,
          onCommit: (v) => set({ body: v }),
          className: "[&_a]:underline [&_img]:h-auto [&_img]:max-w-full [&_p:last-child]:mb-0 [&_p]:mb-3",
          style: {
            fontFamily: c.fontFamily || "Arial, sans-serif",
            fontSize: c.fontSize ?? 14,
            lineHeight: c.lineHeight ?? 1.7,
            color: c.textColor ?? "#111827",
            fontWeight: c.fontWeight ?? 400,
            textDecoration: c.underline ? "underline" : undefined,
          },
          placeholder: "Write something…",
        })}
        {!!c.linkLabel && (
          <div className="mt-2.5">
            {inline ? (
              text({
                value: c.linkLabel,
                plain: true,
                onCommit: (v) => set({ linkLabel: v }),
                className: "inline-block underline",
                style: { fontFamily: "Arial, sans-serif", fontSize: 13, color: c.textColor ?? "#111827" },
              })
            ) : (
              <a
                href={c.linkHref}
                className="underline"
                style={{ fontFamily: "Arial, sans-serif", fontSize: 13, color: c.textColor ?? "#111827" }}
              >
                {c.linkLabel}
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  if (block.type === "image") {
    const width = c.widthPct ?? 100;
    const height = c.imageHeight ?? 260;

    const startResize = (axis: "x" | "y") => (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const startY = e.clientY;
      const containerWidth = handle.parentElement?.parentElement?.clientWidth || 400;
      // Auto-height images start from their rendered height.
      const startHeight = c.autoHeight ? handle.parentElement?.querySelector("img")?.clientHeight || height : height;
      const key = `${block.id}:resize-${axis}`;
      const move = (ev: PointerEvent) => {
        if (axis === "x") {
          // Image is centred, so it grows on both sides — double the delta.
          const next = clamp(width + ((ev.clientX - startX) * 2 * 100) / containerWidth, 20, 100);
          onPatch(block.id, { widthPct: Math.round(next) }, key);
        } else {
          onPatch(
            block.id,
            { imageHeight: Math.round(clamp(startHeight + ev.clientY - startY, 80, 800)), autoHeight: false },
            key
          );
        }
      };
      const up = () => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", up);
        handle.removeEventListener("pointercancel", up);
      };
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", up);
      handle.addEventListener("pointercancel", up);
    };

    return (
      <div style={{ ...wrapper, textAlign: "center" }}>
        <div className="relative mx-auto" style={{ width: `${width}%`, maxWidth: "100%" }}>
          <img
            src={c.src || PLACEHOLDER_IMG}
            alt={c.alt ?? ""}
            draggable={false}
            className="block w-full select-none"
            style={{
              height: c.autoHeight ? "auto" : height,
              objectFit: c.imageFit ?? "cover",
              borderRadius: c.imageRadius ?? 10,
            }}
          />
          {selected && (
            <>
              <button
                type="button"
                aria-label="Drag to resize width"
                onPointerDown={startResize("x")}
                onClick={stop}
                className="absolute -right-2 top-1/2 h-9 w-3.5 -translate-y-1/2 cursor-ew-resize touch-none rounded-full border-2 border-white bg-gray-900 shadow"
              />
              <button
                type="button"
                aria-label="Drag to resize height"
                onPointerDown={startResize("y")}
                onClick={stop}
                className="absolute -bottom-2 left-1/2 h-3.5 w-9 -translate-x-1/2 cursor-ns-resize touch-none rounded-full border-2 border-white bg-gray-900 shadow"
              />
              <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
                {width}% × {c.autoHeight ? "auto" : `${height}px`}
              </span>
            </>
          )}
        </div>
        {selected && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5" onClick={stop}>
            <ImageUploadButton onUploaded={(src) => set({ src })} onUploadImage={onUploadImage} />
            <UrlPopoverButton icon={ImageIcon} label="Image URL" value={c.src} onApply={(src) => set({ src })} />
            <UrlPopoverButton
              icon={Link2}
              label={c.imageLinkHref ? "Edit link" : "Add link"}
              value={c.imageLinkHref}
              onApply={(v) => set({ imageLinkHref: v })}
            />
            {!c.autoHeight && (
              <ChipButton onClick={() => set({ imageFit: c.imageFit === "contain" ? "cover" : "contain" })}>
                <Crop size={12} />
                {c.imageFit === "contain" ? "Fit: contain" : "Fit: crop"}
              </ChipButton>
            )}
          </div>
        )}
      </div>
    );
  }

  if (block.type === "columns") {
    const cols = c.columns ?? [defaultColumn("Column one"), defaultColumn("Column two")];
    return (
      <div style={wrapper}>
        <div
          className="grid"
          style={{ gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", columnGap: c.columnGap ?? 24, rowGap: 16 }}
        >
          {cols.map((col, i) => {
            const updateColumn = (patch: Partial<ColumnContent>, key: string) => {
              const columns = [...cols] as [ColumnContent, ColumnContent];
              columns[i] = { ...columns[i], ...patch };
              set({ columns }, `col${i}:${key}`);
            };
            const color = col.textColor ?? "#111827";
            return (
              <div
                key={i}
                style={{ textAlign: col.align ?? "left", background: bgOrUndefined(col.backgroundColor), borderRadius: 6 }}
              >
                {col.image && (
                  <img src={col.image} alt={col.imageAlt ?? ""} draggable={false} className="mb-2.5 block w-full rounded-[8px]" />
                )}
                {selected && (
                  <div className="mb-2 flex flex-wrap gap-1" onClick={stop}>
                    <ImageUploadButton
                      label={col.image ? "Replace" : "Image"}
                      onUploaded={(src) => updateColumn({ image: src }, "image")}
                      onUploadImage={onUploadImage}
                    />
                    <UrlPopoverButton
                      icon={ImageIcon}
                      label="URL"
                      value={col.image}
                      onApply={(v) => updateColumn({ image: v || undefined }, "image")}
                    />
                    {col.image && (
                      <ChipButton onClick={() => updateColumn({ image: undefined }, "image")}>
                        <X size={11} />
                        Remove
                      </ChipButton>
                    )}
                    {col.buttonLabel ? (
                      <UrlPopoverButton
                        label="Button link"
                        value={col.buttonHref}
                        onApply={(v) => updateColumn({ buttonHref: v }, "buttonHref")}
                      />
                    ) : (
                      <ChipButton
                        onClick={() =>
                          updateColumn({ buttonLabel: "Learn more", buttonHref: col.buttonHref ?? "https://example.com" }, "button")
                        }
                      >
                        <Plus size={11} />
                        Button
                      </ChipButton>
                    )}
                  </div>
                )}
                {(inline || col.title) &&
                  text({
                    value: col.title,
                    plain: true,
                    onCommit: (v) => updateColumn({ title: v }, "title"),
                    className: "mb-1.5",
                    style: { fontFamily: "Arial, sans-serif", fontSize: 16, lineHeight: 1.4, fontWeight: 700, color },
                    placeholder: "Column title",
                  })}
                {(inline || col.body) &&
                  text({
                    value: col.body,
                    onCommit: (v) => updateColumn({ body: v }, "body"),
                    style: { fontFamily: "Arial, sans-serif", fontSize: 13, lineHeight: 1.6, color },
                    placeholder: "Column text",
                  })}
                {!!col.buttonLabel &&
                  text({
                    value: col.buttonLabel,
                    plain: true,
                    onCommit: (v) => updateColumn({ buttonLabel: v }, "buttonLabel"),
                    className: "mt-2.5 inline-block rounded-[6px] px-4 py-2",
                    style: { fontFamily: "Arial, sans-serif", fontSize: 12, fontWeight: 700, color: "#ffffff", background: "#111827" },
                  })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (block.type === "button") {
    return (
      <div style={wrapper}>
        {text({
          value: c.label,
          plain: true,
          onCommit: (v) => set({ label: v }),
          className: "inline-block rounded-[8px] px-5 py-2.5",
          style: {
            fontFamily: "Arial, sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: c.buttonTextColor ?? "#ffffff",
            background: c.buttonBg ?? "#111827",
          },
          placeholder: "Button",
        })}
        {selected && (
          <div className="mt-2 flex flex-wrap gap-1.5" style={{ justifyContent: c.align === "center" ? "center" : c.align === "right" ? "flex-end" : "flex-start" }} onClick={stop}>
            <UrlPopoverButton
              label={c.href && c.href !== "#" ? c.href.replace(/^https?:\/\//, "") : "Add link"}
              value={c.href}
              onApply={(v) => set({ href: v })}
            />
          </div>
        )}
      </div>
    );
  }

  if (block.type === "divider") {
    return (
      <div style={wrapper}>
        <div className="border-t border-gray-200" />
      </div>
    );
  }

  if (block.type === "html") {
    // Rendered as-is (already sanitised); edited from the panel, not inline.
    return <div style={{ ...wrapper, overflowX: "auto" }} dangerouslySetInnerHTML={{ __html: c.html ?? "" }} />;
  }

  if (block.type === "footer") {
    return (
      <div style={wrapper}>
        {text({
          value: c.address,
          plain: true,
          onCommit: (v) => set({ address: v }),
          className: "mb-1",
          style: { fontFamily: "Arial, sans-serif", fontSize: 12, color: "#6b7280" },
          placeholder: "Company address",
        })}
        {text({
          value: c.unsub,
          plain: true,
          onCommit: (v) => set({ unsub: v }),
          className: "underline",
          style: { fontFamily: "Arial, sans-serif", fontSize: 12, color: "#374151" },
          placeholder: "Unsubscribe",
        })}
      </div>
    );
  }

  return null;
}

/* ================================================================== */
/*  Canvas                                                            */
/* ================================================================== */

type DropTarget = { index: number; anchor: string };
type DragPayload = { kind: "new"; type: BlockType } | { kind: "move"; from: number };

const hasBlockDrag = (e: ReactDragEvent) => Array.from(e.dataTransfer.types).includes(DND_MIME);
const readDrag = (e: ReactDragEvent): DragPayload | null => {
  try {
    return JSON.parse(e.dataTransfer.getData(DND_MIME)) as DragPayload;
  } catch {
    return null;
  }
};

function DropLine({ edge }: { edge: "top" | "bottom" }) {
  return (
    <div
      className={cx(
        "pointer-events-none absolute inset-x-0 z-20 h-[3px] rounded-full bg-blue-500",
        edge === "top" ? "-top-[2px]" : "-bottom-[2px]"
      )}
    />
  );
}

function RowActions({
  canUp,
  canDown,
  onUp,
  onDown,
  onDuplicate,
  onDelete,
  dark,
}: {
  canUp: boolean;
  canDown: boolean;
  onUp: () => void;
  onDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  dark?: boolean;
}) {
  const size = dark ? 12 : 14;
  return (
    <>
      <IconButton dark={dark} label="Move up" disabled={!canUp} onClick={onUp}>
        <ChevronUp size={size} />
      </IconButton>
      <IconButton dark={dark} label="Move down" disabled={!canDown} onClick={onDown}>
        <ChevronDown size={size} />
      </IconButton>
      <IconButton dark={dark} label="Duplicate" onClick={onDuplicate}>
        <Copy size={size} />
      </IconButton>
      <IconButton dark={dark} danger label="Delete" onClick={onDelete}>
        <Trash2 size={size} />
      </IconButton>
    </>
  );
}

function Canvas({
  doc,
  device,
  inline,
  selectedId,
  onSelect,
  onPatch,
  onInsert,
  onMove,
  onReorder,
  onDuplicate,
  onDelete,
  onUploadImage,
}: {
  doc: Doc;
  device: Device;
  inline: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onPatch: PatchFn;
  onInsert: (type: BlockType, index: number) => void;
  onMove: (from: number, to: number) => void;
  onReorder: (id: string, delta: number) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onUploadImage?: UploadFn;
}) {
  const [drop, setDrop] = useState<DropTarget | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(scrollRef);
  // Render the stacked mobile layout for the phone preview — or whenever the canvas itself is narrow.
  const mobile = device === "mobile" || (width > 0 && width < 480);
  const groups = useMemo(() => groupIntoSections(doc.blocks, doc.theme), [doc]);
  const total = doc.blocks.length;
  const frameMax =
    device === "mobile"
      ? 375
      : Math.max(doc.theme.cardWidth, ...groups.map((g) => g.marker.innerWidth ?? doc.theme.cardWidth)) + 32;

  useEffect(() => {
    const clear = () => setDrop(null);
    window.addEventListener("dragend", clear);
    window.addEventListener("drop", clear);
    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("drop", clear);
    };
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const el = scrollRef.current?.querySelector(`[data-block-id="${selectedId}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedId]);

  const resolve = (e: ReactDragEvent<HTMLElement>, before: DropTarget, after?: DropTarget) => {
    if (!after) return before;
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY - r.top > r.height / 2 ? after : before;
  };

  const over = (before: DropTarget, after?: DropTarget) => (e: ReactDragEvent<HTMLElement>) => {
    if (!hasBlockDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = e.dataTransfer.effectAllowed === "copy" ? "copy" : "move";
    const target = resolve(e, before, after);
    setDrop((prev) => (prev?.anchor === target.anchor ? prev : target));
  };

  const dropAt = (before: DropTarget, after?: DropTarget) => (e: ReactDragEvent<HTMLElement>) => {
    if (!hasBlockDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    const target = resolve(e, before, after);
    const payload = readDrag(e);
    setDrop(null);
    if (!payload) return;
    if (payload.kind === "new") onInsert(payload.type, target.index);
    else onMove(payload.from, target.index);
  };

  const startMove = (index: number) => (e: ReactDragEvent<HTMLElement>) => {
    e.stopPropagation();
    e.dataTransfer.setData(DND_MIME, JSON.stringify({ kind: "move", from: index } satisfies DragPayload));
    e.dataTransfer.setData("text/plain", "");
    e.dataTransfer.effectAllowed = "move";
    const row = e.currentTarget.closest("[data-row]");
    if (row) e.dataTransfer.setDragImage(row, 16, 16);
  };

  const renderRow = (block: Block, index: number) => {
    const selected = selectedId === block.id;
    const before: DropTarget = { index, anchor: `${block.id}:before` };
    const after: DropTarget = { index: index + 1, anchor: `${block.id}:after` };
    return (
      <div
        key={block.id}
        data-row
        data-block-id={block.id}
        className={cx(
          "group relative cursor-pointer",
          !selected && "hover:outline hover:outline-1 hover:-outline-offset-1 hover:outline-gray-300"
        )}
        style={selected ? { outline: "2px solid #111827", outlineOffset: -2 } : undefined}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(block.id);
        }}
        onDragOver={over(before, after)}
        onDrop={dropAt(before, after)}
      >
        {drop?.anchor === before.anchor && <DropLine edge="top" />}
        {drop?.anchor === after.anchor && <DropLine edge="bottom" />}

        <div
          className={cx(
            "absolute left-1 top-1 z-10 hidden items-center gap-1 transition-opacity lg:flex",
            selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
        >
          <span
            draggable
            onDragStart={startMove(index)}
            title="Drag to move"
            className="flex h-6 w-6 cursor-grab items-center justify-center rounded-md bg-gray-900 text-white active:cursor-grabbing"
          >
            <GripVertical size={12} />
          </span>
          <span className="rounded-md bg-gray-900 px-1.5 py-1 text-[10px] font-medium leading-none text-white">
            {blockMeta(block.type).label}
          </span>
        </div>
        <div
          className={cx(
            "absolute right-1 top-1 z-10 hidden gap-0.5 transition-opacity lg:flex",
            selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
        >
          <RowActions
            dark
            canUp={index > 0}
            canDown={index < total - 1}
            onUp={() => onReorder(block.id, -1)}
            onDown={() => onReorder(block.id, 1)}
            onDuplicate={() => onDuplicate(block.id)}
            onDelete={() => onDelete(block.id)}
          />
        </div>

        <BlockView
          block={block}
          mobile={mobile}
          inline={inline}
          selected={selected}
          onPatch={onPatch}
          onUploadImage={onUploadImage}
        />
      </div>
    );
  };

  const renderSectionBar = (g: Group) => {
    const id = g.markerId as string;
    const index = g.markerIndex;
    const selected = selectedId === id;
    const before: DropTarget = { index, anchor: `${id}:before` };
    const after: DropTarget = { index: index + 1, anchor: `${id}:after` };
    return (
      <div
        data-row
        data-block-id={id}
        className={cx(
          "group relative mx-auto mb-2 flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-[11px]",
          selected ? "border-gray-900 bg-white" : "border-transparent bg-white/60 hover:border-gray-300"
        )}
        style={{ maxWidth: g.marker.innerWidth ?? doc.theme.cardWidth }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}
        onDragOver={over(before, after)}
        onDrop={dropAt(before, after)}
      >
        {drop?.anchor === before.anchor && <DropLine edge="top" />}
        {drop?.anchor === after.anchor && <DropLine edge="bottom" />}
        <span
          draggable
          onDragStart={startMove(index)}
          title="Drag to move section"
          className="hidden cursor-grab text-gray-400 hover:text-gray-900 lg:inline-flex"
        >
          <GripVertical size={13} />
        </span>
        <LayoutTemplate size={12} className="shrink-0 text-gray-500" />
        <span className="font-semibold">Section</span>
        <span className="truncate text-gray-500">{g.marker.sectionTitle}</span>
        <span
          className={cx(
            "ml-auto hidden items-center gap-0.5 transition-opacity lg:flex",
            selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
        >
          <RowActions
            dark
            canUp={index > 0}
            canDown={sectionRange(doc.blocks, index)[1] < total}
            onUp={() => onReorder(id, -1)}
            onDown={() => onReorder(id, 1)}
            onDuplicate={() => onDuplicate(id)}
            onDelete={() => onDelete(id)}
          />
        </span>
      </div>
    );
  };

  const endTarget: DropTarget = { index: total, anchor: "end" };

  return (
    <div
      ref={scrollRef}
      className="min-h-[420px] min-w-0 bg-gray-100 p-3 sm:p-5 lg:min-h-0 lg:overflow-auto"
      onClick={() => onSelect(null)}
      onClickCapture={(e) => {
        // Links on the canvas are for show — never navigate away from the editor.
        if ((e.target as HTMLElement).closest("a")) e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrop(null);
      }}
    >
      {inline && (
        <div className="sticky top-0 z-30 mx-auto mb-3" style={{ maxWidth: frameMax }} onClick={(e) => e.stopPropagation()}>
          <RichTextToolbar />
        </div>
      )}

      <div
        className={cx(
          "mx-auto transition-[max-width] duration-300",
          device === "mobile" && "overflow-hidden rounded-[28px] border-[6px] border-gray-900 shadow-xl"
        )}
        style={{ maxWidth: frameMax }}
      >
        <div className="shadow-sm" style={{ background: doc.theme.pageBg }}>
          {groups.length === 0 && (
            <div className="p-4 sm:p-6">
              <div
                onDragOver={over(endTarget)}
                onDrop={dropAt(endTarget)}
                className={cx(
                  "flex flex-col items-center gap-3 rounded-xl border-2 border-dashed bg-white px-6 py-14 text-center",
                  drop?.anchor === "end" ? "border-blue-500 bg-blue-50" : "border-gray-300"
                )}
              >
                <Mail size={22} className="text-gray-400" />
                <div className="text-[13px] font-medium">Your email is empty</div>
                <div className="text-[12px] text-muted-foreground">Drag a block here, or pick one to start.</div>
                <InsertMenu onPick={(type) => onInsert(type, 0)} label="Add first block" />
              </div>
            </div>
          )}

          {groups.map((g) => {
            const insertIndex = g.items.length ? g.items[g.items.length - 1].index + 1 : g.markerIndex + 1;
            const emptyTarget: DropTarget = { index: g.markerIndex + 1, anchor: `${g.markerId}:empty` };
            return (
              <div
                key={g.markerId ?? "lead"}
                style={{
                  background: bgOrUndefined(g.marker.backgroundColor),
                  padding: `${g.marker.paddingY ?? doc.theme.pagePadding}px ${mobile ? 8 : 16}px`,
                }}
              >
                {g.markerId && renderSectionBar(g)}
                <div
                  className="mx-auto overflow-hidden"
                  style={{
                    background: g.marker.innerBg ?? doc.theme.cardBg,
                    borderRadius: g.marker.innerRadius ?? doc.theme.cardRadius,
                    maxWidth: g.marker.innerWidth ?? doc.theme.cardWidth,
                  }}
                >
                  {g.items.map((it) => renderRow(it.block, it.index))}
                  {!g.items.length && (
                    <div
                      onDragOver={over(emptyTarget)}
                      onDrop={dropAt(emptyTarget)}
                      className={cx(
                        "m-3 rounded-lg border-2 border-dashed px-4 py-8 text-center text-[12px] text-gray-500",
                        drop?.anchor === emptyTarget.anchor ? "border-blue-500 bg-blue-50" : "border-gray-200"
                      )}
                    >
                      Empty section — drop a block here
                    </div>
                  )}
                </div>
                <div className="mx-auto mt-2" style={{ maxWidth: g.marker.innerWidth ?? doc.theme.cardWidth }}>
                  <InsertMenu onPick={(type) => onInsert(type, insertIndex)} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Block library                                                     */
/* ================================================================== */

function BlockLibrary({ onAdd }: { onAdd: (type: BlockType) => void }) {
  return (
    <nav aria-label="Blocks" className="min-w-0 border-b bg-white p-2 md:col-span-2 lg:col-span-1 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:p-3">
      <div className="mb-1 hidden text-[11px] uppercase tracking-wide text-muted-foreground lg:block">Blocks</div>
      <p className="mb-3 hidden text-[11px] leading-4 text-muted-foreground lg:block">
        Drag onto the email, or click to add below the selected block.
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
        {BLOCK_TYPES.map(({ type, label, icon: Icon, desc }) => (
          <button
            key={type}
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DND_MIME, JSON.stringify({ kind: "new", type } satisfies DragPayload));
              e.dataTransfer.setData("text/plain", label);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => onAdd(type)}
            className="flex shrink-0 items-center gap-2 rounded-[10px] border bg-gray-50 px-2.5 py-2 text-left transition hover:bg-gray-100 active:scale-[0.98] lg:cursor-grab"
          >
            <Icon size={15} className="shrink-0" />
            <span>
              <span className="block whitespace-nowrap text-[12.5px] font-medium">{label}</span>
              <span className="hidden text-[10.5px] text-muted-foreground lg:block">{desc}</span>
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
}

/* ================================================================== */
/*  Property panel                                                    */
/* ================================================================== */

function PanelShell({
  icon: Icon,
  title,
  actions,
  onClose,
  children,
}: {
  icon: LucideIcon;
  title: string;
  actions?: ReactNode;
  onClose?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col">
      <div className="hidden">
        <span className="h-1 w-10 rounded-full bg-gray-300" />
      </div>
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-white px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Icon size={15} className="shrink-0" />
          <span className="truncate text-[14px] font-semibold">{title}</span>
        </div>
        <div className="flex items-center gap-0.5">
          {actions}
          {onClose && (
            <IconButton label="Close panel" onClick={onClose}>
              <X size={16} />
            </IconButton>
          )}
        </div>
      </div>
      <div className="flex-1 px-4 pb-8">{children}</div>
    </div>
  );
}

function TypographyFields({
  c,
  set,
  sizes,
  defaultFont,
}: {
  c: BlockContent;
  set: SetFn;
  sizes: number[];
  defaultFont: string;
}) {
  return (
    <>
      <Field label="Text colour">
        <ColorPicker value={c.textColor} onChange={(v) => set({ textColor: v })} />
      </Field>
      <Field label="Font">
        <FontSelect value={c.fontFamily} fallback={defaultFont} onChange={(v) => set({ fontFamily: v })} />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Size">
          <NumberSelect value={c.fontSize} onChange={(v) => set({ fontSize: v })} options={sizes} />
        </Field>
        <Field label="Weight">
          <WeightSelect value={c.fontWeight} onChange={(v) => set({ fontWeight: v })} />
        </Field>
      </div>
      <Field label="Alignment">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AlignPicker value={c.align} onChange={(v) => set({ align: v })} />
          <label className="flex items-center gap-1.5 text-[12px]">
            <input type="checkbox" checked={!!c.underline} onChange={(e) => set({ underline: e.target.checked })} />
            Underline
          </label>
        </div>
      </Field>
    </>
  );
}

function SpacingGroup({ c, set, defaultOpen }: { c: BlockContent; set: SetFn; defaultOpen?: boolean }) {
  return (
    <Group title="Spacing & background" defaultOpen={defaultOpen ?? false}>
      <RangeField label="Padding left / right" value={c.paddingX ?? 32} min={0} max={64} onChange={(v) => set({ paddingX: v })} />
      <RangeField label="Padding top / bottom" value={c.paddingY ?? 10} min={0} max={64} onChange={(v) => set({ paddingY: v })} />
      <RangeField label="Corner radius" value={c.wrapperRadius ?? 0} min={0} max={40} onChange={(v) => set({ wrapperRadius: v })} />
      <Field label="Background">
        <ColorPicker allowTransparent value={c.backgroundColor} onChange={(v) => set({ backgroundColor: v })} />
      </Field>
    </Group>
  );
}

/** Panel field that looks like an input but keeps formatting (same editor as the canvas). */
function RichInput({
  value,
  onCommit,
  placeholder,
  multiline,
  onUploadImage,
}: {
  value?: string;
  onCommit: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  onUploadImage?: UploadFn;
}) {
  return (
    <EditableRichText
      value={value ?? ""}
      onCommit={onCommit}
      plain={!multiline}
      placeholder={placeholder}
      onUploadImage={onUploadImage}
      className={cx(
        "input block h-auto py-2 leading-6 focus:ring-0 [&_img]:inline-block [&_img]:h-auto [&_img]:max-h-24 [&_img]:max-w-full [&_a]:underline [&_p]:mb-2 [&_p:last-child]:mb-0",
        multiline ? "min-h-[80px]" : "min-h-[38px]"
      )}
    />
  );
}

/** Code editor for HTML blocks. Sanitised and committed on blur (and on unmount). */
function HtmlBlockField({ value = "", onCommit }: { value?: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const latest = useRef({ draft, value, onCommit });
  latest.current = { draft, value, onCommit };

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  const commit = useCallback(() => {
    const { draft: d, value: v, onCommit: send } = latest.current;
    const clean = sanitizeFragment(d);
    if (clean !== v) send(clean);
    return clean;
  }, []);

  useEffect(
    () => () => {
      if (latest.current.draft !== latest.current.value) commit();
    },
    [commit]
  );

  return (
    <textarea
      aria-label="Block HTML"
      spellCheck={false}
      className="input block h-64 w-full resize-y py-2 font-mono text-[11.5px] leading-5"
      value={draft}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        focused.current = false;
        setDraft(commit());
      }}
    />
  );
}

function ColumnFields({
  column,
  index,
  onChange,
  onUploadImage,
}: {
  column: ColumnContent;
  index: number;
  onChange: (patch: Partial<ColumnContent>, key: string) => void;
  onUploadImage?: UploadFn;
}) {
  const set = (patch: Partial<ColumnContent>) => onChange(patch, Object.keys(patch).join(","));
  return (
    <Group title={`Column ${index + 1}`} defaultOpen={index === 0}>
      <Field label="Title">
        <RichInput value={column.title} onCommit={(v) => set({ title: v })} onUploadImage={onUploadImage} />
      </Field>
      <Field label="Body">
        <RichInput multiline value={column.body} onCommit={(v) => set({ body: normalizeBodyHtml(v) })} onUploadImage={onUploadImage} />
      </Field>
      <Field label="Image">
        <div className="flex flex-wrap items-center gap-1.5">
          <ImageUploadButton
            label={column.image ? "Replace" : "Upload"}
            onUploaded={(src) => set({ image: src })}
            onUploadImage={onUploadImage}
          />
          {column.image && (
            <ChipButton onClick={() => set({ image: undefined })}>
              <X size={11} />
              Remove
            </ChipButton>
          )}
        </div>
        <div className="mt-2">
          <UrlInput value={column.image} onChange={(v) => set({ image: v || undefined })} placeholder="…or paste an image URL" />
        </div>
      </Field>
      {column.image && (
        <>
          <Field label="Image alt text">
            <input className="input" value={column.imageAlt ?? ""} onChange={(e) => set({ imageAlt: e.target.value })} />
          </Field>
          <Field label="Image link">
            <UrlInput value={column.imageHref} onChange={(v) => set({ imageHref: v || undefined })} />
          </Field>
        </>
      )}
      <div className="grid grid-cols-1 gap-x-2 sm:grid-cols-2 lg:grid-cols-1">
        <Field label="Button label">
          <RichInput
            placeholder="Leave empty for no button"
            value={column.buttonLabel}
            onCommit={(v) => set({ buttonLabel: v || undefined })}
          />
        </Field>
        <Field label="Button URL">
          <UrlInput value={column.buttonHref} onChange={(v) => set({ buttonHref: v })} />
        </Field>
      </div>
      <Field label="Text colour">
        <ColorPicker value={column.textColor} onChange={(v) => set({ textColor: v })} />
      </Field>
      <Field label="Background">
        <ColorPicker allowTransparent value={column.backgroundColor} onChange={(v) => set({ backgroundColor: v })} />
      </Field>
      <Field label="Alignment">
        <AlignPicker value={column.align} onChange={(v) => set({ align: v })} />
      </Field>
    </Group>
  );
}

function PropertyPanel({
  block,
  index,
  total,
  blocks,
  theme,
  onPatch,
  onThemePatch,
  onDelete,
  onDuplicate,
  onReorder,
  onConvertHtml,
  onClose,
  onUploadImage,
}: {
  block: Block | null;
  index: number;
  total: number;
  blocks: Block[];
  theme: Theme;
  onPatch: PatchFn;
  onThemePatch: (patch: Partial<Theme>, key?: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onReorder: (id: string, delta: number) => void;
  onConvertHtml: (id: string) => void;
  onClose?: () => void;
  onUploadImage?: UploadFn;
}) {
  if (!block) {
    return (
      <PanelShell icon={Palette} title="Email settings" onClose={onClose}>
        <Group title="Page">
          <Field label="Email background">
            <ColorPicker value={theme.pageBg} onChange={(v) => onThemePatch({ pageBg: v }, "pageBg")} />
          </Field>
        </Group>
        <Group title="All sections" hint="Applies to every section. Select a section on the canvas to fine-tune just that one.">
          <Field label="Card background">
            <ColorPicker value={theme.cardBg} onChange={(v) => onThemePatch({ cardBg: v }, "cardBg")} />
          </Field>
          <RangeField label="Max width" value={theme.cardWidth} min={320} max={900} onChange={(v) => onThemePatch({ cardWidth: v }, "cardWidth")} />
          <RangeField label="Corner radius" value={theme.cardRadius} min={0} max={40} onChange={(v) => onThemePatch({ cardRadius: v }, "cardRadius")} />
          <RangeField label="Outer padding" value={theme.pagePadding} min={0} max={80} onChange={(v) => onThemePatch({ pagePadding: v }, "pagePadding")} />
        </Group>
        <p className="mt-4 text-[12px] text-muted-foreground">Select a block on the canvas to edit it.</p>
        <div className="mt-4 hidden rounded-[10px] bg-gray-50 p-3 text-[11.5px] leading-6 text-muted-foreground lg:block">
          <div className="mb-1 font-semibold text-gray-700">Shortcuts</div>
          {SHORTCUTS.map(([keys, desc]) => (
            <div key={keys} className="flex justify-between gap-3">
              <span>{desc}</span>
              <kbd className="font-mono text-[11px]">{keys}</kbd>
            </div>
          ))}
        </div>
      </PanelShell>
    );
  }

  const c = block.content;
  const set: SetFn = (patch, key) => onPatch(block.id, patch, `${block.id}:${key ?? Object.keys(patch).join(",")}`);
  const meta = blockMeta(block.type);
  const canDown = block.type === "section" ? sectionRange(blocks, index)[1] < total : index < total - 1;

  const actions = (
    <RowActions
      canUp={index > 0}
      canDown={canDown}
      onUp={() => onReorder(block.id, -1)}
      onDown={() => onReorder(block.id, 1)}
      onDuplicate={() => onDuplicate(block.id)}
      onDelete={() => onDelete(block.id)}
    />
  );

  return (
    <PanelShell
      icon={meta.icon}
      title={block.type === "section" ? c.sectionTitle || "Section" : meta.label}
      actions={actions}
      onClose={onClose}
    >
      {block.type === "header" && (
        <>
          <Group title="Content">
            <Field label="Kicker">
              <RichInput placeholder="Small label above the title" value={c.kicker} onCommit={(v) => set({ kicker: v })} onUploadImage={onUploadImage} />
            </Field>
            <Field label="Title">
              <RichInput value={c.title} onCommit={(v) => set({ title: v })} onUploadImage={onUploadImage} />
            </Field>
          </Group>
          <Group title="Style">
            <TypographyFields c={c} set={set} sizes={[18, 20, 22, 24, 26, 28, 32, 36]} defaultFont={HEADING_FONT} />
          </Group>
        </>
      )}

      {block.type === "text" && (
        <>
          <Group title="Content">
            <Field label="Body">
              <div className="overflow-hidden rounded-[12px] border bg-white">
                <RichTextToolbar compact />
                <EditableRichText
                  value={c.body ?? ""}
                  normalize={normalizeBodyHtml}
                  onCommit={(v) => set({ body: v })}
                  className="min-h-[140px] px-3 py-2 text-[14px] leading-7 focus:ring-0 [&_img]:h-auto [&_img]:max-w-full [&_a]:text-blue-600 [&_a]:underline [&_p:last-child]:mb-0 [&_p]:mb-3"
                  placeholder="Write your message…"
                  onUploadImage={onUploadImage}
                />
              </div>
            </Field>
            <Field label="Link label">
              <RichInput placeholder="Optional link below the text" value={c.linkLabel} onCommit={(v) => set({ linkLabel: v })} />
            </Field>
            {!!c.linkLabel && (
              <Field label="Link URL">
                <UrlInput value={c.linkHref} onChange={(v) => set({ linkHref: v })} />
              </Field>
            )}
          </Group>
          <Group title="Style">
            <TypographyFields c={c} set={set} sizes={[12, 13, 14, 15, 16, 18, 20]} defaultFont={BODY_FONT} />
          </Group>
        </>
      )}

      {block.type === "image" && (
        <>
          <Group title="Image">
            <div className="mb-3 overflow-hidden rounded-[10px] border bg-gray-50">
              <img src={c.src || PLACEHOLDER_IMG} alt="" className="block h-28 w-full object-contain" />
            </div>
            <Field
              label="Source"
              hint={!onUploadImage ? "Uploads are embedded in the email. Hosted URLs are more reliable — Gmail blocks embedded images." : undefined}
            >
              <div className="mb-2">
                <ImageUploadButton onUploaded={(src) => set({ src })} onUploadImage={onUploadImage} />
              </div>
              <UrlInput value={c.src} onChange={(v) => set({ src: v })} placeholder="…or paste an image URL" />
            </Field>
            <Field label="Alt text" hint="Shown when images are blocked, and read by screen readers.">
              <input className="input" value={c.alt ?? ""} onChange={(e) => set({ alt: e.target.value })} />
            </Field>
            <Field label="Link when clicked">
              <UrlInput value={c.imageLinkHref} onChange={(v) => set({ imageLinkHref: v })} />
            </Field>
          </Group>
          <Group title="Size">
            <RangeField label="Width" unit="%" value={c.widthPct ?? 100} min={20} max={100} onChange={(v) => set({ widthPct: v })} />
            <Field label="Height">
              <label className="flex items-center gap-1.5 text-[12px]">
                <input type="checkbox" checked={!!c.autoHeight} onChange={(e) => set({ autoHeight: e.target.checked })} />
                Auto — keep the image's proportions
              </label>
            </Field>
            {!c.autoHeight && (
              <>
                <RangeField label="Fixed height" value={c.imageHeight ?? 260} min={80} max={800} onChange={(v) => set({ imageHeight: v })} />
                <Field label="Fit">
                  <div className="inline-flex rounded-[10px] bg-gray-100 p-1">
                    {(["cover", "contain"] as const).map((fit) => (
                      <button
                        key={fit}
                        type="button"
                        aria-pressed={(c.imageFit ?? "cover") === fit}
                        onClick={() => set({ imageFit: fit })}
                        className={cx(
                          "rounded-[8px] px-3 py-1 text-[12px] font-medium",
                          (c.imageFit ?? "cover") === fit ? "bg-white shadow-sm" : "text-muted-foreground"
                        )}
                      >
                        {fit === "cover" ? "Crop to fill" : "Show whole image"}
                      </button>
                    ))}
                  </div>
                </Field>
              </>
            )}
            <RangeField label="Image corners" value={c.imageRadius ?? 10} min={0} max={40} onChange={(v) => set({ imageRadius: v })} />
          </Group>
        </>
      )}

      {block.type === "columns" && (
        <>
          <Group title="Layout" hint="Columns stack on phones automatically.">
            <RangeField label="Gap between columns" value={c.columnGap ?? 24} min={0} max={64} onChange={(v) => set({ columnGap: v })} />
          </Group>
          {[0, 1].map((i) => {
            const columns = c.columns ?? [defaultColumn("Column one"), defaultColumn("Column two")];
            return (
              <ColumnFields
                key={i}
                index={i}
                column={columns[i]}
                onUploadImage={onUploadImage}
                onChange={(patch, key) => {
                  const next = [...columns] as [ColumnContent, ColumnContent];
                  next[i] = { ...next[i], ...patch };
                  set({ columns: next }, `col${i}:${key}`);
                }}
              />
            );
          })}
        </>
      )}

      {block.type === "button" && (
        <>
          <Group title="Content">
            <Field label="Label">
              <RichInput value={c.label} onCommit={(v) => set({ label: v })} onUploadImage={onUploadImage} />
            </Field>
            <Field label="Link URL">
              <UrlInput value={c.href} onChange={(v) => set({ href: v })} />
            </Field>
          </Group>
          <Group title="Style">
            <Field label="Button colour">
              <ColorPicker value={c.buttonBg} onChange={(v) => set({ buttonBg: v })} />
            </Field>
            <Field label="Label colour">
              <ColorPicker value={c.buttonTextColor} onChange={(v) => set({ buttonTextColor: v })} />
            </Field>
            <Field label="Alignment">
              <AlignPicker value={c.align} onChange={(v) => set({ align: v })} />
            </Field>
          </Group>
        </>
      )}

      {block.type === "footer" && (
        <>
          <Group title="Content">
            <Field label="Address">
              <RichInput value={c.address} onCommit={(v) => set({ address: v })} onUploadImage={onUploadImage} />
            </Field>
            <Field label="Unsubscribe label">
              <RichInput value={c.unsub} onCommit={(v) => set({ unsub: v })} />
            </Field>
          </Group>
          <Group title="Style">
            <Field label="Alignment">
              <AlignPicker value={c.align} onChange={(v) => set({ align: v })} />
            </Field>
          </Group>
        </>
      )}

      {block.type === "html" && (
        <Group title="Code" hint="Sent exactly as written. Use inline styles — <style> tags and scripts are removed.">
          <HtmlBlockField key={block.id} value={c.html} onCommit={(v) => set({ html: v })} />
          <button
            type="button"
            onClick={() => onConvertHtml(block.id)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-[8px] border bg-white px-3 py-1.5 text-[12px] font-medium text-gray-700 hover:border-gray-900"
          >
            <LayoutTemplate size={13} />
            Convert to editable blocks
          </button>
        </Group>
      )}

      {block.type === "section" && (
        <>
          <Group title="Section">
            <Field label="Name" hint="Only shown in the editor.">
              <input className="input" value={c.sectionTitle ?? ""} onChange={(e) => set({ sectionTitle: e.target.value })} />
            </Field>
            <Field label="Full-width background">
              <ColorPicker allowTransparent value={c.backgroundColor} onChange={(v) => set({ backgroundColor: v })} />
            </Field>
            <RangeField label="Padding top / bottom" value={c.paddingY ?? 24} min={0} max={80} onChange={(v) => set({ paddingY: v })} />
          </Group>
          <Group title="Card">
            <Field label="Card background">
              <ColorPicker allowTransparent value={c.innerBg} onChange={(v) => set({ innerBg: v })} />
            </Field>
            <RangeField label="Corner radius" value={c.innerRadius ?? 12} min={0} max={40} onChange={(v) => set({ innerRadius: v })} />
            <RangeField label="Max width" value={c.innerWidth ?? 600} min={320} max={900} onChange={(v) => set({ innerWidth: v })} />
          </Group>
        </>
      )}

      {block.type === "divider" && <p className="py-3 text-[12px] text-muted-foreground">A thin line to separate content.</p>}

      {block.type !== "section" && <SpacingGroup c={c} set={set} defaultOpen={block.type === "divider"} />}
    </PanelShell>
  );
}

/* ================================================================== */
/*  HTML source                                                       */
/* ================================================================== */

function HtmlSource({ html, onApply }: { html: string; onApply: (html: string) => void }) {
  const [draft, setDraft] = useState(html);
  const [pending, setPending] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ draft, pending, onApply });
  latest.current = { draft, pending, onApply };

  // Apply any unsynced edits when leaving HTML mode.
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      if (latest.current.pending) latest.current.onApply(latest.current.draft);
    },
    []
  );

  const update = (v: string) => {
    setDraft(v);
    setPending(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      onApply(v);
      setPending(false);
    }, 500);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const foreign = !!draft.trim() && !isBuilderHtml(draft);

  return (
    <div className="overflow-hidden rounded-[15px] border bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gray-50 px-3 py-2 text-[12px]">
        <span className={cx("inline-flex items-center gap-1.5", pending ? "text-amber-600" : "text-emerald-600")}>
          {pending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {pending ? "Applying…" : "Synced with visual builder"}
        </span>
        <div className="flex items-center gap-3 text-muted-foreground">
          <span>{formatKb(byteSize(draft))}</span>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-gray-700 hover:border-gray-900"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
      {foreign && (
        <div className="flex items-start gap-2 border-b bg-blue-50 px-3 py-2 text-[12px] text-blue-900">
          <Info size={14} className="mt-0.5 shrink-0" />
          This HTML wasn't made with the visual builder, so it's been converted into editable blocks. Parts that
          couldn't be mapped are kept as HTML blocks — open the visual builder to check the result.
        </div>
      )}
      <textarea
        aria-label="Email HTML source"
        spellCheck={false}
        className="block h-[60vh] w-full resize-y p-3 font-mono text-[12px] leading-5 outline-none lg:h-[560px]"
        value={draft}
        onChange={(e) => update(e.target.value)}
        placeholder="Paste your email HTML here..."
      />
    </div>
  );
}

/* ================================================================== */
/*  Editor                                                            */
/* ================================================================== */

type Toast = { id: number; message: string; undo?: boolean };

const TemplateEditor = ({ value, onChange, onUploadImage }: TemplateEditorProps) => {
  const [initialDoc] = useState<Doc>(() => docFromHtml(value));
  const { doc, apply, undo, redo, reset, canUndo, canRedo } = useDocHistory(initialDoc);

  const [mode, setMode] = useState<Mode>("blocks");
  const [device, setDevice] = useState<Device>("desktop");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false); // phone drawer
  const [panelCollapsed, setPanelCollapsed] = useState(false); // docked panel (tablet/desktop)
  const openPanel = useCallback(() => {
    setSheetOpen(true);
    setPanelCollapsed(false);
  }, []);
  const closePanel = useCallback(() => {
    setSheetOpen(false);
    setPanelCollapsed(true);
  }, []);
  const [toast, setToast] = useState<Toast | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Guarded: parents sometimes render the editor without a handler (read-only views,
  // a form library field that isn't wired yet). Never crash on a missing callback.
  const emitChange = useCallback((html: string) => {
    const handler = onChangeRef.current;
    if (typeof handler === "function") handler(html);
  }, []);
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  // Last HTML we reported upward — lets us ignore our own value echoing back as a prop.
  const lastEmitted = useRef<string>(value ?? exportHtml(initialDoc.blocks, initialDoc.theme));
  // A doc whose HTML is already known (initial load / raw HTML import) and shouldn't be re-exported.
  const silentDoc = useRef<Doc | null>(initialDoc);

  const exported = useMemo(() => exportHtml(doc.blocks, doc.theme), [doc]);
  const currentHtml = doc === silentDoc.current ? lastEmitted.current : exported;
  const bytes = useMemo(() => byteSize(currentHtml), [currentHtml]);

  useEffect(() => {
    if (doc === silentDoc.current) return;
    silentDoc.current = null;
    if (exported === lastEmitted.current) return;
    lastEmitted.current = exported;
    emitChange(exported);
  }, [doc, exported, emitChange]);

  // Only genuinely external value changes reload the editor (keeps selection + undo intact).
  useEffect(() => {
    if (value === undefined || value === lastEmitted.current) return;
    const cleaned = cleanImportedHtml(value);
    if (cleaned === lastEmitted.current) return;
    const parsed = parseHtml(cleaned);
    if (!parsed) return;
    lastEmitted.current = value;
    silentDoc.current = parsed;
    reset(parsed);
    setSelectedId(null);
  }, [value, reset]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  /* ---------------------------- operations ---------------------------- */

  const patchBlock = useCallback<PatchFn>(
    (id, patch, key) =>
      apply(
        (d) => ({ ...d, blocks: d.blocks.map((b) => (b.id === id ? { ...b, content: { ...b.content, ...patch } } : b)) }),
        key
      ),
    [apply]
  );

  const patchTheme = useCallback(
    (patch: Partial<Theme>, key?: string) => apply((d) => applyThemePatch(d, patch), key ? `theme:${key}` : undefined),
    [apply]
  );

  const insertBlock = useCallback(
    (type: BlockType, index?: number) => {
      const id = nextId();
      apply((d) => {
        const selIndex = selectedRef.current ? d.blocks.findIndex((b) => b.id === selectedRef.current) : -1;
        let at = index ?? (selIndex === -1 ? d.blocks.length : selIndex + 1);
        const content =
          type === "section"
            ? {
                ...defaultContent("section"),
                innerBg: d.theme.cardBg,
                innerRadius: d.theme.cardRadius,
                innerWidth: d.theme.cardWidth,
                paddingY: d.theme.pagePadding,
              }
            : defaultContent(type);
        if (type === "section") at = snapToGroupBoundary(d.blocks, at);
        const blocks = [...d.blocks];
        blocks.splice(clamp(at, 0, blocks.length), 0, { id, type, content });
        return { ...d, blocks };
      });
      setSelectedId(id);
    },
    [apply]
  );

  const moveBlock = useCallback(
    (from: number, to: number) => apply((d) => ({ ...d, blocks: moveBlocks(d.blocks, from, to) })),
    [apply]
  );

  const reorderBlock = useCallback(
    (id: string, delta: number) => apply((d) => ({ ...d, blocks: reorderBlocks(d.blocks, id, delta) })),
    [apply]
  );

  const deleteBlock = useCallback(
    (id: string) => {
      const index = doc.blocks.findIndex((b) => b.id === id);
      if (index === -1) return;
      const [start, end] = sectionRange(doc.blocks, index);
      const removedType = doc.blocks[index].type;

      apply((d) => {
        const i = d.blocks.findIndex((b) => b.id === id);
        if (i === -1) return d;
        const [s, e] = sectionRange(d.blocks, i);
        return { ...d, blocks: [...d.blocks.slice(0, s), ...d.blocks.slice(e)] };
      });

      const remaining = [...doc.blocks.slice(0, start), ...doc.blocks.slice(end)];
      setSelectedId(remaining[start]?.id ?? remaining[start - 1]?.id ?? null);
      setSheetOpen(false);

      const inner = end - start - 1;
      setToast({
        id: Date.now(),
        message:
          removedType === "section" && inner > 0
            ? `Section and ${inner} block${inner === 1 ? "" : "s"} deleted`
            : `${blockMeta(removedType).label} deleted`,
        undo: true,
      });
    },
    [apply, doc.blocks]
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const index = doc.blocks.findIndex((b) => b.id === id);
      if (index === -1) return;
      const [start, end] = sectionRange(doc.blocks, index);
      const copies = doc.blocks.slice(start, end).map((b) => ({ ...b, id: nextId(), content: cloneContent(b.content) }));
      apply((d) => {
        const i = d.blocks.findIndex((b) => b.id === id);
        if (i === -1) return d;
        const [, e] = sectionRange(d.blocks, i);
        const blocks = [...d.blocks];
        blocks.splice(e, 0, ...copies);
        return { ...d, blocks };
      });
      setSelectedId(copies[0].id);
    },
    [apply, doc.blocks]
  );

  /** Breaks an HTML block down into native blocks where possible. */
  const convertHtmlBlock = useCallback(
    (id: string) => {
      const block = doc.blocks.find((b) => b.id === id);
      if (!block || block.type !== "html") return;
      const width = doc.theme.cardWidth;
      const result = importForeignHtml(
        `<!DOCTYPE html><html><body style="margin:0"><div style="width:${width}px;margin:0 auto;background:${doc.theme.cardBg};">${block.content.html ?? ""}</div></body></html>`
      );
      const pieces = result?.blocks.filter((b) => b.type !== "section") ?? [];
      if (!pieces.length || (pieces.length === 1 && pieces[0].type === "html")) {
        setToast({ id: Date.now(), message: "This HTML can't be broken down any further" });
        return;
      }
      apply((d) => {
        const i = d.blocks.findIndex((b) => b.id === id);
        if (i === -1) return d;
        const blocks = [...d.blocks];
        blocks.splice(i, 1, ...pieces);
        return { ...d, blocks };
      });
      setSelectedId(pieces[0].id);
      setToast({ id: Date.now(), message: `Converted into ${pieces.length} block${pieces.length === 1 ? "" : "s"}`, undo: true });
    },
    [apply, doc.blocks, doc.theme.cardWidth, doc.theme.cardBg]
  );

  const importHtml = useCallback(
    (raw: string) => {
      const normalized = cleanImportedHtml(raw);
      const parsed = parseHtml(normalized);
      if (!parsed) return;
      setSelectedId(null);

      if (!isBuilderHtml(normalized)) {
        // Foreign HTML: the converted doc becomes the source of truth and is
        // exported (and reported via onChange) in the builder's own format.
        apply(() => parsed);
        return;
      }

      silentDoc.current = parsed;
      lastEmitted.current = normalized;
      apply(() => parsed);
      emitChange(normalized);
    },
    [apply, emitChange]
  );

  const selectBlock = useCallback((id: string | null) => {
    setSelectedId(id);
    // Focus the editor so keyboard shortcuts work — unless the user is typing in a field.
    if (!isTypingTarget(document.activeElement)) rootRef.current?.focus({ preventScroll: true });
  }, []);

  /* ---------------------------- merge tags ---------------------------- */

  const restoreRange = useSavedRteRange();
  const insertMergeTag = (tag: string) => {
    if (restoreRange(true)) {
      document.execCommand("insertText", false, tag);
      return;
    }
    navigator.clipboard?.writeText(tag).then(
      () => setToast({ id: Date.now(), message: `Copied ${tag} — paste it into any field` }),
      () => setToast({ id: Date.now(), message: "Click into a text field first, then pick a tag" })
    );
  };

  /* ----------------------------- keyboard ----------------------------- */

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const typing = isTypingTarget(e.target);
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();

    // Don't let Enter in a panel field submit a surrounding <form>.
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      return;
    }
    if (mod && key === "z" && !typing) {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
      return;
    }
    if (mod && key === "y" && !typing) {
      e.preventDefault();
      redo();
      return;
    }
    if (e.key === "Escape") {
      if (typing) {
        (e.target as HTMLElement).blur();
        rootRef.current?.focus({ preventScroll: true });
      } else if (sheetOpen) setSheetOpen(false);
      else setSelectedId(null);
      return;
    }
    if (typing || mode === "html" || !selectedId) return;

    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      deleteBlock(selectedId);
    } else if (mod && key === "d") {
      e.preventDefault();
      duplicateBlock(selectedId);
    } else if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      reorderBlock(selectedId, e.key === "ArrowUp" ? -1 : 1);
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const i = doc.blocks.findIndex((b) => b.id === selectedId);
      const next = doc.blocks[i + (e.key === "ArrowUp" ? -1 : 1)];
      if (next) setSelectedId(next.id);
    }
  };

  /* ------------------------------ render ------------------------------ */

  const selectedIndex = selectedId ? doc.blocks.findIndex((b) => b.id === selectedId) : -1;
  const selectedBlock = selectedIndex === -1 ? null : doc.blocks[selectedIndex];
  const hasEmbeddedImages = currentHtml.includes('src="data:');

  const segBtn = (active: boolean) =>
    cx(
      "flex items-center gap-1.5 rounded-[8px] px-2.5 py-1.5 text-[12.5px] font-medium transition",
      active ? "bg-white shadow-sm" : "text-muted-foreground hover:text-gray-900"
    );

  return (
    <div ref={rootRef} tabIndex={-1} onKeyDown={onKeyDown} className="space-y-2 outline-none">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="label">Content</span>
        <div className="flex flex-wrap items-center gap-2">
          {mode !== "html" && (
            <>
              <div className="flex items-center rounded-[10px] bg-gray-100 p-1">
                <button type="button" className={segBtn(false)} disabled={!canUndo} onClick={undo} title="Undo (⌘/Ctrl Z)" aria-label="Undo">
                  <Undo2 size={14} className={cx(!canUndo && "opacity-30")} />
                </button>
                <button type="button" className={segBtn(false)} disabled={!canRedo} onClick={redo} title="Redo (⇧ ⌘/Ctrl Z)" aria-label="Redo">
                  <Redo2 size={14} className={cx(!canRedo && "opacity-30")} />
                </button>
              </div>
              <div className="flex items-center rounded-[10px] bg-gray-100 p-1" role="group" aria-label="Preview width">
                <button
                  type="button"
                  className={segBtn(device === "desktop")}
                  aria-pressed={device === "desktop"}
                  onClick={() => setDevice("desktop")}
                  title="Desktop preview"
                  aria-label="Desktop preview"
                >
                  <Monitor size={14} />
                </button>
                <button
                  type="button"
                  className={segBtn(device === "mobile")}
                  aria-pressed={device === "mobile"}
                  onClick={() => setDevice("mobile")}
                  title="Mobile preview"
                  aria-label="Mobile preview"
                >
                  <Smartphone size={14} />
                </button>
              </div>
              <button
                type="button"
                className={cx(segBtn(false), "bg-gray-100")}
                onClick={() => {
                  setSelectedId(null);
                  openPanel();
                }}
                title="Email settings"
              >
                <Palette size={14} />
                <span className="hidden sm:inline">Theme</span>
              </button>
              <button
                type="button"
                className={cx(segBtn(false), "hidden bg-gray-100 md:flex")}
                onClick={() => (panelCollapsed ? openPanel() : closePanel())}
                title={panelCollapsed ? "Show settings panel" : "Hide settings panel"}
                aria-pressed={!panelCollapsed}
              >
                {panelCollapsed ? <PanelRightOpen size={14} /> : <PanelRightClose size={14} />}
                <span className="hidden lg:inline">{panelCollapsed ? "Show panel" : "Hide panel"}</span>
              </button>
            </>
          )}
          <div className="flex items-center rounded-[10px] bg-gray-100 p-1" role="tablist" aria-label="Editor mode">
            {[
              { key: "blocks" as Mode, label: "Visual builder", Icon: LayoutTemplate },
              { key: "preview" as Mode, label: "Edit inline", Icon: Pencil },
              { key: "html" as Mode, label: "HTML", Icon: Code2 },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={mode === key}
                title={label}
                onClick={() => {
                  setMode(key);
                  setSheetOpen(false);
                }}
                className={segBtn(mode === key)}
              >
                <Icon size={13} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {mode === "html" ? (
        <HtmlSource html={currentHtml} onApply={importHtml} />
      ) : (
        <div
          className={cx(
            "relative flex flex-col overflow-clip rounded-[15px] border bg-white md:grid lg:h-[680px] lg:overflow-hidden",
            panelCollapsed
              ? "md:grid-cols-[minmax(0,1fr)] lg:grid-cols-[200px_minmax(0,1fr)]"
              : "md:grid-cols-[minmax(0,1fr)_280px] lg:grid-cols-[200px_minmax(0,1fr)_300px]"
          )}
        >
          <BlockLibrary onAdd={(type) => insertBlock(type)} />

          <Canvas
            doc={doc}
            device={device}
            inline={mode === "preview"}
            selectedId={selectedId}
            onSelect={selectBlock}
            onPatch={patchBlock}
            onInsert={insertBlock}
            onMove={moveBlock}
            onReorder={reorderBlock}
            onDuplicate={duplicateBlock}
            onDelete={deleteBlock}
            onUploadImage={onUploadImage}
          />

          {/* Properties: slide-in side drawer on phones (no backdrop, so the email stays
              scrollable and tappable behind it); docked right column from md up. */}
          <aside
            aria-label="Properties"
            className={cx(
              "bg-white",
              sheetOpen
                ? "fixed inset-y-0 right-0 z-50 w-[min(320px,85vw)] overflow-y-auto overscroll-contain border-l shadow-2xl"
                : "hidden",
              "md:sticky md:top-0 md:z-auto md:block md:max-h-screen md:w-auto md:self-start md:overflow-y-auto md:shadow-none",
              "lg:static lg:max-h-none lg:self-stretch",
              panelCollapsed && "md:hidden"
            )}
          >
            <PropertyPanel
              block={selectedBlock}
              index={selectedIndex}
              total={doc.blocks.length}
              blocks={doc.blocks}
              theme={doc.theme}
              onPatch={patchBlock}
              onThemePatch={patchTheme}
              onDelete={deleteBlock}
              onDuplicate={duplicateBlock}
              onReorder={reorderBlock}
              onConvertHtml={convertHtmlBlock}
              onClose={closePanel}
              onUploadImage={onUploadImage}
            />
          </aside>
        </div>
      )}

      {/* Footer: merge tags + size */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
          <span>Merge tags:</span>
          {MERGE_TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              title="Insert at cursor (or copy)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertMergeTag(tag)}
              className="rounded-md border bg-white px-2 py-0.5 font-mono text-[11px] text-gray-700 hover:border-gray-900"
            >
              {tag}
            </button>
          ))}
        </div>
        <span className={cx("inline-flex items-center gap-1 text-[12px]", bytes > GMAIL_CLIP_BYTES ? "text-amber-600" : "text-muted-foreground")}>
          {bytes > GMAIL_CLIP_BYTES && <AlertTriangle size={13} />}
          {formatKb(bytes)}
          {bytes > GMAIL_CLIP_BYTES &&
            ` — Gmail clips emails over 102 KB${hasEmbeddedImages ? "; embedded images are the likely cause" : ""}`}
        </span>
      </div>

      {/* Mobile action bar for the selected block */}
      {mode !== "html" && selectedBlock && !sheetOpen && (
        <div className={cx("fixed inset-x-0 bottom-3 z-30 flex justify-center px-3", !panelCollapsed && "md:hidden")}>
          <div className="flex items-center gap-0.5 rounded-full bg-gray-900 py-1 pl-3 pr-1 text-white shadow-xl">
            <span className="mr-1 max-w-[90px] truncate text-[12px] font-medium">{blockMeta(selectedBlock.type).label}</span>
            {(
              [
                { label: "Edit", icon: Pencil, onClick: openPanel },
                { label: "Move up", icon: ChevronUp, onClick: () => reorderBlock(selectedBlock.id, -1), disabled: selectedIndex <= 0 },
                {
                  label: "Move down",
                  icon: ChevronDown,
                  onClick: () => reorderBlock(selectedBlock.id, 1),
                  disabled: selectedIndex >= doc.blocks.length - 1,
                },
                { label: "Duplicate", icon: Copy, onClick: () => duplicateBlock(selectedBlock.id) },
                { label: "Delete", icon: Trash2, onClick: () => deleteBlock(selectedBlock.id) },
                { label: "Done", icon: X, onClick: () => setSelectedId(null) },
              ] as { label: string; icon: LucideIcon; onClick: () => void; disabled?: boolean }[]
            ).map(({ label, icon: Icon, onClick, disabled }) => (
              <button
                key={label}
                type="button"
                aria-label={label}
                title={label}
                disabled={disabled}
                onClick={onClick}
                className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-30"
              >
                <Icon size={16} />
              </button>
            ))}
          </div>
        </div>
      )}

      {toast && (
        <div
          key={toast.id}
          role="status"
          className="fixed bottom-20 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full bg-gray-900 px-4 py-2 text-[12px] text-white shadow-xl lg:bottom-6"
        >
          {toast.message}
          {toast.undo && (
            <button
              type="button"
              className="font-semibold underline underline-offset-2"
              onClick={() => {
                undo();
                setToast(null);
              }}
            >
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;

// Exposed for tests / server-free tooling.
export { exportHtml, parseHtml, importForeignHtml, sanitizeFragment };